import { z } from "zod";

//#region src/constraints.ts
/**
* String length constraints and validation patterns for input validation
*
* These constants define security-focused limits to prevent:
* - Memory exhaustion from oversized payloads (DoS)
* - Database bloat from unbounded field sizes
* - Buffer overflow issues
* - Log injection attacks
*
* @see https://owasp.org/www-community/controls/Input_Validation
*/
/**
* Maximum string lengths for different field types
*
* Categories:
* - Short: Labels, titles, names (UI-displayed, single line)
* - Medium: Descriptions, summaries (multi-line, bounded)
* - Large: Content, messages (main payload data)
* - IDs: Fixed-length identifiers
*/
const STRING_LIMITS = {
	/** Max title length (session titles, artifact titles) */
	TITLE_MAX: 256,
	/** Max name length (user names, machine names, server names) */
	NAME_MAX: 128,
	/** Max label/tag length (short identifiers) */
	LABEL_MAX: 64,
	/** Max username length (login identifiers) */
	USERNAME_MAX: 64,
	/** Max description length (session descriptions, bios) */
	DESCRIPTION_MAX: 4096,
	/** Max summary length (short descriptions) */
	SUMMARY_MAX: 1024,
	/** Max bio length (user profile bios) */
	BIO_MAX: 500,
	/** Max content length for encrypted payloads (1MB) */
	CONTENT_MAX: 1e6,
	/** Max message content length (100KB) */
	MESSAGE_MAX: 1e5,
	/** Max encrypted state length (500KB) */
	ENCRYPTED_STATE_MAX: 5e5,
	/** Max versioned value content (500KB) */
	VERSIONED_VALUE_MAX: 5e5,
	/** Standard UUID length (36 chars with hyphens) */
	UUID_LENGTH: 36,
	/** Max token/key length (for encryption keys, API tokens) */
	TOKEN_MAX: 8192,
	/** Max data encryption key length (base64 encoded NaCl key) */
	DATA_ENCRYPTION_KEY_MAX: 256,
	/** Max session/machine/artifact ID length */
	ID_MAX: 128,
	/** Max local ID length (client-generated IDs) */
	LOCAL_ID_MAX: 128,
	/** Max URL length */
	URL_MAX: 2048,
	/** Max file path length */
	PATH_MAX: 1024,
	/** Max thumbhash length (base64 encoded placeholder image) */
	THUMBHASH_MAX: 256,
	/** Max KV key length */
	KV_KEY_MAX: 256,
	/** Max KV value length */
	KV_VALUE_MAX: 65536,
	/** Max MCP server name length */
	MCP_SERVER_NAME_MAX: 128,
	/** Max MCP tool name length */
	MCP_TOOL_NAME_MAX: 128,
	/** Max MCP tool description length */
	MCP_TOOL_DESCRIPTION_MAX: 2048,
	/** Max feed post text length */
	FEED_TEXT_MAX: 4096,
	/** Max cursor string length */
	CURSOR_MAX: 256,
	/** Max repeat key length */
	REPEAT_KEY_MAX: 128
};
/**
* Validation patterns for format checking
*
* Note: These are security-focused patterns. For GitHub specifically,
* we validate username format but allow passthrough for additional fields.
*/
const PATTERNS = {
	/** URL slug pattern (lowercase alphanumeric with hyphens) */
	SLUG: /^[a-z0-9-]+$/,
	/** Basic email format validation */
	EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
	/** Username pattern (alphanumeric with underscores and hyphens) */
	USERNAME: /^[a-zA-Z0-9_-]+$/,
	/** GitHub login pattern (alphanumeric with hyphens, no leading/trailing) */
	GITHUB_LOGIN: /^[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/,
	/** Safe string pattern (no control characters except newline/tab) */
	SAFE_STRING: /^[^\x00-\x08\x0B\x0C\x0E-\x1F]*$/
};
/**
* Request body size limits for server-level validation
*
* These are enforced at the server level before schema validation.
* Schema-level limits provide defense in depth.
*/
const BODY_SIZE_LIMITS = {
	/** Default max request body (5MB) */
	DEFAULT: 5 * 1024 * 1024,
	/** Max body for file uploads (50MB) */
	UPLOAD: 50 * 1024 * 1024,
	/** Max body for sync payloads (10MB) */
	SYNC: 10 * 1024 * 1024,
	/** Max body for admin API (1MB) */
	ADMIN: 1 * 1024 * 1024
};

//#endregion
//#region src/common.ts
/**
* Common types shared across Happy protocol
*
* These are foundational types used by multiple update and ephemeral schemas.
*
* Security: All string fields have maximum length constraints to prevent
* DoS attacks via oversized payloads and database bloat.
*
* @see ./constraints.ts for STRING_LIMITS constants
*/
/**
* GitHub profile data from OAuth
* Used in update-account events
*
* IMPORTANT: This is the CANONICAL schema - all projects must import from here.
*
* GitHub API field requirements:
* - id: Always present (required)
* - login: Always present (required)
* - name: User-settable, can be null or missing
* - avatar_url: Usually present but not guaranteed
* - email: User preference, can be null or missing
* - bio: Optional user field, can be null or missing
*
* We use .strip() to safely ignore additional GitHub fields while preventing
* prototype pollution and storage bloat attacks.
*
* @example
* ```typescript
* const profile = GitHubProfileSchema.parse({
*     id: 12345678,
*     login: 'octocat',
*     name: 'The Octocat',
*     avatar_url: 'https://avatars.githubusercontent.com/u/12345678',
*     email: 'octocat@github.com',
*     bio: 'I love coding!'
* });
* ```
*/
const GitHubProfileSchema = z.object({
	id: z.number(),
	login: z.string().min(1).max(STRING_LIMITS.USERNAME_MAX),
	name: z.string().max(STRING_LIMITS.NAME_MAX).nullable().optional(),
	avatar_url: z.string().max(STRING_LIMITS.URL_MAX).optional(),
	email: z.string().max(STRING_LIMITS.NAME_MAX).nullable().optional(),
	bio: z.string().max(STRING_LIMITS.BIO_MAX).nullable().optional()
}).strip();
/**
* Build an OpenAPI-ready `GitHubProfile` schema using a Zod instance from
* `@hono/zod-openapi`. This avoids adding a hard runtime dependency on
* `@hono/zod-openapi` to the canonical package while still providing a
* single source of truth for schema constraints.
*
* Example:
*   import { z } from '@hono/zod-openapi';
*   import { makeOpenApiGitHubProfileSchema } from '@happy/protocol';
*   const openApiSchema = makeOpenApiGitHubProfileSchema(z);
*/
function makeOpenApiGitHubProfileSchema(zOpenApi) {
	return zOpenApi.object({
		id: zOpenApi.number().openapi({
			description: "GitHub numeric id",
			example: 12345678
		}),
		login: zOpenApi.string().min(1).max(STRING_LIMITS.USERNAME_MAX).openapi({
			description: "GitHub login",
			example: "octocat"
		}),
		name: zOpenApi.string().max(STRING_LIMITS.NAME_MAX).nullable().optional().openapi({
			description: "Display name",
			example: "The Octocat"
		}),
		avatar_url: zOpenApi.string().max(STRING_LIMITS.URL_MAX).optional().openapi({ description: "Avatar URL" }),
		email: zOpenApi.string().max(STRING_LIMITS.NAME_MAX).nullable().optional().openapi({ description: "Email address" }),
		bio: zOpenApi.string().max(STRING_LIMITS.BIO_MAX).nullable().optional().openapi({ description: "Bio" })
	}).strip().openapi("GitHubProfile");
}
/**
* Image reference for avatars and other media
*
* Note: width, height, and thumbhash are optional because:
* - Image dimensions may not be available at upload time
* - Thumbhash is generated asynchronously and may not exist yet
*
* @example
* ```typescript
* const avatar = ImageRefSchema.parse({
*     path: 'avatars/user123/profile.jpg',
*     url: 'https://cdn.example.com/avatars/user123/profile.jpg',
*     width: 256,
*     height: 256,
*     thumbhash: 'YJqGPQw7WGdweIeAeH...'
* });
* ```
*/
const ImageRefSchema = z.object({
	path: z.string().min(1).max(STRING_LIMITS.PATH_MAX),
	url: z.string().min(1).max(STRING_LIMITS.URL_MAX),
	width: z.number().optional(),
	height: z.number().optional(),
	thumbhash: z.string().max(STRING_LIMITS.THUMBHASH_MAX).optional()
});
/**
* Relationship status between users
*
* @example
* ```typescript
* const status = RelationshipStatusSchema.parse('friend');
* // Valid values: 'none', 'requested', 'pending', 'friend', 'rejected'
* ```
*/
const RelationshipStatusSchema = z.enum([
	"none",
	"requested",
	"pending",
	"friend",
	"rejected"
]);
/**
* User profile for social features
*
* @example
* ```typescript
* const user = UserProfileSchema.parse({
*     id: 'user_abc123',
*     firstName: 'Jane',
*     lastName: 'Doe',
*     avatar: null,
*     username: 'janedoe',
*     bio: 'Software developer',
*     status: 'friend'
* });
* ```
*/
const UserProfileSchema = z.object({
	id: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	firstName: z.string().min(1).max(STRING_LIMITS.NAME_MAX),
	lastName: z.string().max(STRING_LIMITS.NAME_MAX).nullable(),
	avatar: ImageRefSchema.nullable(),
	username: z.string().min(1).max(STRING_LIMITS.USERNAME_MAX),
	bio: z.string().max(STRING_LIMITS.BIO_MAX).nullable(),
	status: RelationshipStatusSchema,
	/** ISO 8601 date when the friendship was accepted (only present for friends) */
	friendshipDate: z.string().nullable().optional()
});
/**
* Feed body types for activity feed
*
* @example
* ```typescript
* // Friend request notification
* const friendRequest = FeedBodySchema.parse({
*     kind: 'friend_request',
*     uid: 'user_xyz789'
* });
*
* // Text notification
* const textPost = FeedBodySchema.parse({
*     kind: 'text',
*     text: 'Welcome to Happy!'
* });
* ```
*/
const FeedBodySchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z.literal("friend_request"),
		uid: z.string().min(1).max(STRING_LIMITS.ID_MAX)
	}),
	z.object({
		kind: z.literal("friend_accepted"),
		uid: z.string().min(1).max(STRING_LIMITS.ID_MAX)
	}),
	z.object({
		kind: z.literal("text"),
		text: z.string().max(STRING_LIMITS.FEED_TEXT_MAX)
	})
]);
/**
* Encrypted message content structure
* Used for all encrypted payloads in the protocol
*
* @example
* ```typescript
* const encrypted = EncryptedContentSchema.parse({
*     t: 'encrypted',
*     c: 'base64EncodedEncryptedContent=='
* });
* ```
*/
const EncryptedContentSchema = z.object({
	t: z.literal("encrypted"),
	c: z.string().max(STRING_LIMITS.CONTENT_MAX)
});
/**
* Versioned value wrapper for optimistic concurrency
* Used for metadata, agentState, daemonState, etc.
*
* @example
* ```typescript
* const versioned = VersionedValueSchema.parse({
*     version: 5,
*     value: '{"key": "encrypted-data"}'
* });
* ```
*/
const VersionedValueSchema = z.object({
	version: z.number(),
	value: z.string().max(STRING_LIMITS.VERSIONED_VALUE_MAX)
});
/**
* Nullable versioned value (for updates where value can be cleared)
*
* @example
* ```typescript
* // Set a value
* const withValue = NullableVersionedValueSchema.parse({
*     version: 3,
*     value: '{"state": "active"}'
* });
*
* // Clear a value
* const cleared = NullableVersionedValueSchema.parse({
*     version: 4,
*     value: null
* });
* ```
*/
const NullableVersionedValueSchema = z.object({
	version: z.number(),
	value: z.string().max(STRING_LIMITS.VERSIONED_VALUE_MAX).nullable()
});

//#endregion
//#region src/updates/message.ts
/**
* Message-related update schemas
*
* Handles: new-message, delete-session (session lifecycle)
*
* Security: All string fields have maximum length constraints.
*/
/**
* API Message schema - encrypted message structure
*
* Messages are stored encrypted; the server cannot read content.
*
* @example
* ```typescript
* const message = ApiMessageSchema.parse({
*     id: 'msg_xyz789',
*     seq: 42,
*     localId: 'local_123',
*     content: { t: 'encrypted', c: 'base64EncryptedContent==' },
*     createdAt: Date.now()
* });
* ```
*/
const ApiMessageSchema = z.object({
	id: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	seq: z.number(),
	localId: z.string().max(STRING_LIMITS.LOCAL_ID_MAX).nullish(),
	content: EncryptedContentSchema,
	createdAt: z.number()
});
/**
* New message update
*
* Contains the message payload and session reference.
*
* @example
* ```typescript
* const newMessage = ApiUpdateNewMessageSchema.parse({
*     t: 'new-message',
*     sid: 'session_abc123',
*     message: {
*         id: 'msg_xyz789',
*         seq: 42,
*         content: { t: 'encrypted', c: 'base64EncryptedContent==' },
*         createdAt: Date.now()
*     }
* });
* ```
*/
const ApiUpdateNewMessageSchema = z.object({
	t: z.literal("new-message"),
	/**
	* Session ID
	*
	* @remarks
	* Field name: `sid` (short for session ID)
	*
	* All session-related schemas now use `sid` for consistency:
	* - `new-session`, `update-session`, `new-message`, `delete-session`: use `sid`
	* - Ephemeral events (`activity`, `usage`): use `sid`
	*
	* @see HAP-654 - Standardization of session ID field names
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	message: ApiMessageSchema
});
/**
* Delete session update
*
* Sent when a session is archived or deleted.
*
* @example
* ```typescript
* const deleteSession = ApiDeleteSessionSchema.parse({
*     t: 'delete-session',
*     sid: 'session_abc123'
* });
* ```
*/
const ApiDeleteSessionSchema = z.object({
	t: z.literal("delete-session"),
	/**
	* Session ID
	*
	* @remarks
	* Field name: `sid` (short for session ID)
	*
	* All session-related schemas now use `sid` for consistency:
	* - `new-session`, `update-session`, `new-message`, `delete-session`: use `sid`
	* - Ephemeral events (`activity`, `usage`): use `sid`
	*
	* @see HAP-654 - Standardization of session ID field names
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX)
});

//#endregion
//#region src/updates/session.ts
/**
* Session-related update schemas
*
* Handles: new-session, update-session
*
* Security: All string fields have maximum length constraints.
*/
/**
* New session update
*
* Sent when a new Claude Code session is created.
* Contains initial encrypted metadata and agent state.
*
* @example
* ```typescript
* const newSession = ApiUpdateNewSessionSchema.parse({
*     t: 'new-session',
*     sid: 'session_abc123',
*     seq: 1,
*     metadata: 'encryptedMetadataString',
*     metadataVersion: 1,
*     agentState: null,
*     agentStateVersion: 0,
*     dataEncryptionKey: 'base64EncodedKey==',
*     active: true,
*     activeAt: Date.now(),
*     createdAt: Date.now(),
*     updatedAt: Date.now()
* });
* ```
*/
const ApiUpdateNewSessionSchema = z.object({
	t: z.literal("new-session"),
	/**
	* Session ID
	*
	* @remarks
	* Field name: `sid` (short for session ID)
	*
	* All session-related schemas now use `sid` for consistency:
	* - `new-session`, `update-session`, `new-message`, `delete-session`: use `sid`
	* - Ephemeral events (`activity`, `usage`): use `sid`
	*
	* @see HAP-654 - Standardization of session ID field names
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	seq: z.number(),
	metadata: z.string().max(STRING_LIMITS.ENCRYPTED_STATE_MAX),
	metadataVersion: z.number(),
	agentState: z.string().max(STRING_LIMITS.ENCRYPTED_STATE_MAX).nullable(),
	agentStateVersion: z.number(),
	dataEncryptionKey: z.string().max(STRING_LIMITS.DATA_ENCRYPTION_KEY_MAX).nullable(),
	active: z.boolean(),
	activeAt: z.number(),
	createdAt: z.number(),
	updatedAt: z.number()
});
/**
* Update session state
*
* Sent when session metadata or agent state changes.
* Both fields are optional - only changed fields are included.
*
* @example
* ```typescript
* const sessionUpdate = ApiUpdateSessionStateSchema.parse({
*     t: 'update-session',
*     sid: 'session_abc123',
*     agentState: { version: 2, value: 'encryptedState' },
*     metadata: { version: 3, value: null }  // Cleared metadata
* });
* ```
*/
const ApiUpdateSessionStateSchema = z.object({
	t: z.literal("update-session"),
	/**
	* Session ID
	*
	* @remarks
	* Field name: `sid` (short for session ID)
	*
	* All session-related schemas now use `sid` for consistency:
	* - `new-session`, `update-session`, `new-message`, `delete-session`: use `sid`
	* - Ephemeral events (`activity`, `usage`): use `sid`
	*
	* @see HAP-654 - Standardization of session ID field names
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	agentState: NullableVersionedValueSchema.nullish(),
	metadata: NullableVersionedValueSchema.nullish()
});
/**
* Archive reason for sessions
*
* @remarks
* - `revival_failed`: Session could not be revived after CLI reconnection
* - `user_requested`: User explicitly requested archival
* - `timeout`: Session timed out due to inactivity
*/
const ArchiveReasonSchema = z.enum([
	"revival_failed",
	"user_requested",
	"timeout"
]);
/**
* Archive session update
*
* Sent when a session is archived (soft-deleted).
* Archived sessions are excluded from active session lists but not permanently deleted.
*
* @example
* ```typescript
* const archiveSession = ApiArchiveSessionSchema.parse({
*     t: 'archive-session',
*     sid: 'session_abc123',
*     archivedAt: Date.now(),
*     archiveReason: 'revival_failed'
* });
* ```
*/
const ApiArchiveSessionSchema = z.object({
	t: z.literal("archive-session"),
	/**
	* Session ID
	*
	* @remarks
	* Field name: `sid` (short for session ID)
	*
	* All session-related schemas now use `sid` for consistency:
	* - `new-session`, `update-session`, `new-message`, `delete-session`, `archive-session`: use `sid`
	* - Ephemeral events (`activity`, `usage`): use `sid`
	*
	* @see HAP-654 - Standardization of session ID field names
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/**
	* Timestamp when the session was archived (Unix milliseconds)
	*/
	archivedAt: z.number(),
	/**
	* Reason for archiving the session
	*/
	archiveReason: ArchiveReasonSchema
});

//#endregion
//#region src/updates/machine.ts
/**
* Machine-related update schemas
*
* Handles: new-machine, update-machine
*
* Security: All string fields have maximum length constraints.
*/
/**
* New machine update
*
* Sent when a new CLI machine is registered.
*
* @example
* ```typescript
* const newMachine = ApiNewMachineSchema.parse({
*     t: 'new-machine',
*     machineId: 'machine_laptop1',
*     seq: 1,
*     metadata: 'encryptedMachineMetadata',
*     metadataVersion: 1,
*     daemonState: null,
*     daemonStateVersion: 0,
*     dataEncryptionKey: 'base64EncodedKey==',
*     active: true,
*     activeAt: Date.now(),
*     createdAt: Date.now(),
*     updatedAt: Date.now()
* });
* ```
*/
const ApiNewMachineSchema = z.object({
	t: z.literal("new-machine"),
	/**
	* Machine ID
	*
	* @remarks
	* Field name: `machineId`
	*
	* All machine-related schemas consistently use `machineId` (HAP-655):
	* - `new-machine`: uses `machineId`
	* - `update-machine`: uses `machineId`
	* - `machine-status`: uses `machineId`
	* - `machine-activity`: uses `machineId`
	*
	* @see ApiEphemeralMachineActivityUpdateSchema
	* @see ApiEphemeralMachineStatusUpdateSchema
	*/
	machineId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	seq: z.number(),
	metadata: z.string().max(STRING_LIMITS.ENCRYPTED_STATE_MAX),
	metadataVersion: z.number(),
	daemonState: z.string().max(STRING_LIMITS.ENCRYPTED_STATE_MAX).nullable(),
	daemonStateVersion: z.number(),
	dataEncryptionKey: z.string().max(STRING_LIMITS.DATA_ENCRYPTION_KEY_MAX).nullable(),
	active: z.boolean(),
	activeAt: z.number(),
	createdAt: z.number(),
	updatedAt: z.number()
});
/**
* Update machine state
*
* Sent when machine metadata or daemon state changes.
*
* @example
* ```typescript
* const machineUpdate = ApiUpdateMachineStateSchema.parse({
*     t: 'update-machine',
*     machineId: 'machine_laptop1',
*     daemonState: { version: 2, value: 'encryptedDaemonState' },
*     active: true,
*     activeAt: Date.now()
* });
* ```
*/
const ApiUpdateMachineStateSchema = z.object({
	t: z.literal("update-machine"),
	/**
	* Machine ID
	*
	* @remarks
	* Field name: `machineId`
	*
	* All machine-related schemas consistently use `machineId` (HAP-655):
	* - `new-machine`: uses `machineId`
	* - `update-machine`: uses `machineId`
	* - `machine-status`: uses `machineId`
	* - `machine-activity`: uses `machineId`
	*
	* @see ApiEphemeralMachineActivityUpdateSchema
	* @see ApiEphemeralMachineStatusUpdateSchema
	*/
	machineId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	metadata: VersionedValueSchema.optional(),
	daemonState: VersionedValueSchema.optional(),
	active: z.boolean().optional(),
	activeAt: z.number().optional()
});
/**
* Delete machine update
*
* Sent when a machine is disconnected/unauthenticated from an account.
* The machine will need to re-authenticate via QR code to reconnect.
*
* @example
* ```typescript
* const deleteMachine = ApiDeleteMachineSchema.parse({
*     t: 'delete-machine',
*     machineId: 'machine_laptop1'
* });
* ```
*/
const ApiDeleteMachineSchema = z.object({
	t: z.literal("delete-machine"),
	/**
	* Machine ID being deleted/disconnected
	*
	* @remarks
	* Field name: `machineId`
	*
	* All machine-related schemas consistently use `machineId` (HAP-655):
	* - `new-machine`: uses `machineId`
	* - `update-machine`: uses `machineId`
	* - `delete-machine`: uses `machineId`
	* - `machine-status`: uses `machineId`
	* - `machine-activity`: uses `machineId`
	*
	* @see HAP-778 - Machine disconnect functionality
	*/
	machineId: z.string().min(1).max(STRING_LIMITS.ID_MAX)
});

//#endregion
//#region src/updates/artifact.ts
/**
* Artifact-related update schemas
*
* Handles: new-artifact, update-artifact, delete-artifact
*
* Security: All string fields have maximum length constraints.
*/
/**
* New artifact update
*
* Sent when a new artifact (file/output) is created.
*
* @example
* ```typescript
* const newArtifact = ApiNewArtifactSchema.parse({
*     t: 'new-artifact',
*     artifactId: 'artifact_code1',
*     header: 'encryptedHeader',
*     headerVersion: 1,
*     body: 'encryptedCodeBody',
*     bodyVersion: 1,
*     dataEncryptionKey: 'base64EncodedKey==',
*     seq: 5,
*     createdAt: Date.now(),
*     updatedAt: Date.now()
* });
* ```
*/
const ApiNewArtifactSchema = z.object({
	t: z.literal("new-artifact"),
	artifactId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	header: z.string().max(STRING_LIMITS.ENCRYPTED_STATE_MAX),
	headerVersion: z.number(),
	body: z.string().max(STRING_LIMITS.CONTENT_MAX).optional(),
	bodyVersion: z.number().optional(),
	dataEncryptionKey: z.string().min(1).max(STRING_LIMITS.DATA_ENCRYPTION_KEY_MAX),
	seq: z.number(),
	createdAt: z.number(),
	updatedAt: z.number()
});
/**
* Update artifact
*
* Sent when artifact header or body changes.
*
* @example
* ```typescript
* const artifactUpdate = ApiUpdateArtifactSchema.parse({
*     t: 'update-artifact',
*     artifactId: 'artifact_code1',
*     body: { version: 2, value: 'updatedEncryptedBody' }
* });
* ```
*/
const ApiUpdateArtifactSchema = z.object({
	t: z.literal("update-artifact"),
	artifactId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	header: VersionedValueSchema.optional(),
	body: VersionedValueSchema.optional()
});
/**
* Delete artifact
*
* Sent when an artifact is deleted.
*
* @example
* ```typescript
* const deleteArtifact = ApiDeleteArtifactSchema.parse({
*     t: 'delete-artifact',
*     artifactId: 'artifact_code1'
* });
* ```
*/
const ApiDeleteArtifactSchema = z.object({
	t: z.literal("delete-artifact"),
	artifactId: z.string().min(1).max(STRING_LIMITS.ID_MAX)
});

//#endregion
//#region src/updates/account.ts
/**
* Account-related update schemas
*
* Handles: update-account
*
* Security: All string fields have maximum length constraints.
*/
/**
* Update account
*
* Sent when user account settings or profile changes.
*
* @example
* ```typescript
* const accountUpdate = ApiUpdateAccountSchema.parse({
*     t: 'update-account',
*     id: 'user_abc123',
*     firstName: 'Jane',
*     lastName: 'Doe',
*     avatar: {
*         path: 'avatars/user_abc123/profile.jpg',
*         url: 'https://cdn.example.com/avatars/user_abc123/profile.jpg'
*     },
*     github: { id: 12345678, login: 'janedoe', name: 'Jane Doe' }
* });
* ```
*/
const ApiUpdateAccountSchema = z.object({
	t: z.literal("update-account"),
	id: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	settings: NullableVersionedValueSchema.nullish(),
	firstName: z.string().max(STRING_LIMITS.NAME_MAX).nullish(),
	lastName: z.string().max(STRING_LIMITS.NAME_MAX).nullish(),
	avatar: ImageRefSchema.nullish(),
	github: GitHubProfileSchema.nullish()
});

//#endregion
//#region src/updates/misc.ts
/**
* Miscellaneous update schemas
*
* Handles: relationship-updated, new-feed-post, kv-batch-update
*
* Security: All string fields have maximum length constraints.
*/
/**
* Relationship update
*
* Sent when a friend relationship status changes.
*
* @example
* ```typescript
* const relationshipUpdate = ApiRelationshipUpdatedSchema.parse({
*     t: 'relationship-updated',
*     fromUserId: 'user_abc123',
*     toUserId: 'user_xyz789',
*     status: 'friend',
*     action: 'created',
*     timestamp: Date.now()
* });
* ```
*/
const ApiRelationshipUpdatedSchema = z.object({
	t: z.literal("relationship-updated"),
	fromUserId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	toUserId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	status: RelationshipStatusSchema,
	action: z.enum([
		"created",
		"updated",
		"deleted"
	]),
	fromUser: UserProfileSchema.optional(),
	toUser: UserProfileSchema.optional(),
	timestamp: z.number()
});
/**
* New feed post
*
* Sent when a new activity feed item is created.
*
* @example
* ```typescript
* const feedPost = ApiNewFeedPostSchema.parse({
*     t: 'new-feed-post',
*     id: 'feed_123',
*     body: { kind: 'friend_request', uid: 'user_xyz789' },
*     cursor: 'cursor_abc',
*     createdAt: Date.now(),
*     repeatKey: null,
*     counter: 1
* });
* ```
*/
const ApiNewFeedPostSchema = z.object({
	t: z.literal("new-feed-post"),
	id: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	body: FeedBodySchema,
	cursor: z.string().max(STRING_LIMITS.CURSOR_MAX),
	createdAt: z.number(),
	repeatKey: z.string().max(STRING_LIMITS.REPEAT_KEY_MAX).nullable(),
	counter: z.number()
});
/**
* KV batch update
*
* Sent when key-value settings change (batch sync).
*
* @example
* ```typescript
* const kvUpdate = ApiKvBatchUpdateSchema.parse({
*     t: 'kv-batch-update',
*     changes: [
*         { key: 'theme', value: 'dark', version: 1 },
*         { key: 'notifications', value: 'enabled', version: 2 },
*         { key: 'deprecated_setting', value: null, version: 3 }  // Deleted
*     ]
* });
* ```
*/
const ApiKvBatchUpdateSchema = z.object({
	t: z.literal("kv-batch-update"),
	changes: z.array(z.object({
		key: z.string().min(1).max(STRING_LIMITS.KV_KEY_MAX),
		value: z.string().max(STRING_LIMITS.KV_VALUE_MAX).nullable(),
		version: z.number()
	}))
});

//#endregion
//#region src/updates/index.ts
/**
* Update schemas barrel export
*
* All persistent update event types for the Happy protocol.
* Updates represent state changes that are stored and synced.
*/
/**
* Discriminated union of all update types
*
* This is the main type for all persistent updates sent via WebSocket.
* Uses 't' (type) as the discriminator field.
*
* @example
* ```typescript
* // Parse any incoming WebSocket update
* const update = ApiUpdateSchema.parse(incomingData);
*
* // Type-safe handling based on discriminator
* switch (update.t) {
*     case 'new-session':
*     case 'new-message':
*     case 'update-session':
*     case 'delete-session':
*         // All session updates use 'sid' (HAP-654)
*         console.log('Session:', update.sid);
*         break;
*     // ... handle other update types
* }
* ```
*/
const ApiUpdateSchema = z.discriminatedUnion("t", [
	ApiUpdateNewMessageSchema,
	ApiUpdateNewSessionSchema,
	ApiDeleteSessionSchema,
	ApiArchiveSessionSchema,
	ApiUpdateSessionStateSchema,
	ApiUpdateAccountSchema,
	ApiUpdateMachineStateSchema,
	ApiNewMachineSchema,
	ApiDeleteMachineSchema,
	ApiNewArtifactSchema,
	ApiUpdateArtifactSchema,
	ApiDeleteArtifactSchema,
	ApiRelationshipUpdatedSchema,
	ApiNewFeedPostSchema,
	ApiKvBatchUpdateSchema
]);

//#endregion
//#region src/ephemeral/events.ts
/**
* Ephemeral event schemas
*
* Ephemeral events are transient status updates that don't need persistence.
* These are real-time indicators of activity (typing, presence, etc.)
*
* Security: All string fields have maximum length constraints.
*/
/**
* Session activity update
*
* Real-time indicator of session activity and thinking state.
*/
const ApiEphemeralActivityUpdateSchema = z.object({
	type: z.literal("activity"),
	/**
	* Session ID
	*
	* @remarks
	* Field name: `sid` (short for session ID)
	*
	* All session-related schemas now use `sid` for consistency:
	* - `new-session`, `update-session`, `new-message`, `delete-session`: use `sid`
	* - Ephemeral events (`activity`, `usage`): use `sid`
	*
	* @see HAP-654 - Standardization of session ID field names
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	active: z.boolean(),
	activeAt: z.number(),
	thinking: z.boolean()
});
/**
* Token/cost usage update
*
* Real-time cost and token tracking for a session.
* Uses flexible Record types to accommodate varying token breakdown keys
* from different AI providers (Claude, Codex, etc.)
*
* Required: `total` key must be present
* Optional: Additional breakdown keys (input, output, cache_creation, cache_read, etc.)
*/
const ApiEphemeralUsageUpdateSchema = z.object({
	type: z.literal("usage"),
	/**
	* Session ID
	*
	* @remarks
	* Field name: `sid` (short for session ID)
	*
	* All session-related schemas now use `sid` for consistency:
	* - `new-session`, `update-session`, `new-message`, `delete-session`: use `sid`
	* - Ephemeral events (`activity`, `usage`): use `sid`
	*
	* @see HAP-654 - Standardization of session ID field names
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	key: z.string().min(1).max(STRING_LIMITS.LABEL_MAX),
	timestamp: z.number(),
	tokens: z.record(z.string().max(STRING_LIMITS.LABEL_MAX), z.number()).refine((obj) => typeof obj.total === "number", { message: "tokens.total is required" }),
	cost: z.record(z.string().max(STRING_LIMITS.LABEL_MAX), z.number()).refine((obj) => typeof obj.total === "number", { message: "cost.total is required" })
});
/**
* Machine activity update
*
* Real-time indicator of machine/daemon activity.
*/
const ApiEphemeralMachineActivityUpdateSchema = z.object({
	type: z.literal("machine-activity"),
	/**
	* Machine ID - uniquely identifies the machine/daemon
	*
	* @remarks
	* Field name: `machineId` (standardized in HAP-655)
	*
	* All machine-related schemas now consistently use `machineId`:
	* - `new-machine`: uses `machineId`
	* - `update-machine`: uses `machineId`
	* - `machine-status`: uses `machineId`
	* - `machine-activity`: uses `machineId`
	*
	* @see ApiNewMachineSchema
	* @see ApiUpdateMachineStateSchema
	* @see ApiEphemeralMachineStatusUpdateSchema
	*/
	machineId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	active: z.boolean(),
	activeAt: z.number()
});
/**
* Machine online status update
*
* Real-time indicator of machine online/offline status.
*/
const ApiEphemeralMachineStatusUpdateSchema = z.object({
	type: z.literal("machine-status"),
	/**
	* Machine ID - uniquely identifies the machine/daemon
	*
	* @remarks
	* Field name: `machineId`
	*
	* All machine-related schemas consistently use `machineId`:
	* - `new-machine`: uses `machineId`
	* - `update-machine`: uses `machineId`
	* - `machine-status`: uses `machineId`
	* - `machine-activity`: uses `machineId`
	*
	* @see ApiNewMachineSchema
	* @see ApiUpdateMachineStateSchema
	* @see ApiEphemeralMachineActivityUpdateSchema
	*/
	machineId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	online: z.boolean(),
	timestamp: z.number()
});
/**
* Machine disconnected notification
*
* Sent to a machine's WebSocket connection when it is disconnected
* from an account (e.g., via the mobile app). This is a fire-and-forget
* notification that allows the CLI daemon to gracefully shut down.
*
* @see HAP-780 - Add CLI disconnect notification when machine is removed via app
*/
const ApiEphemeralMachineDisconnectedUpdateSchema = z.object({
	type: z.literal("machine-disconnected"),
	/**
	* Machine ID - uniquely identifies the machine being disconnected
	*
	* @remarks
	* Field name: `machineId` (standardized in HAP-655)
	*/
	machineId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/**
	* Reason for disconnection
	*
	* Currently always 'disconnected_by_user' when user disconnects
	* a machine via the mobile app.
	*/
	reason: z.enum(["disconnected_by_user"]),
	/**
	* Timestamp of when the disconnection occurred
	*/
	timestamp: z.number()
});
/**
* Friend online/offline status update
*
* Real-time indicator of friend presence status.
* Sent to a user's friends when they come online or go offline.
*
* @see HAP-716 - Implement real-time friend online status
*/
const ApiEphemeralFriendStatusUpdateSchema = z.object({
	type: z.literal("friend-status"),
	/**
	* User ID of the friend whose status changed
	*
	* @remarks
	* This is the ID of the user who came online/went offline,
	* not the recipient. The event is sent TO friends, containing
	* this user's status.
	*/
	userId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/**
	* Whether the user is currently online
	*
	* True when the user has at least one active WebSocket connection.
	* False when all connections are closed.
	*/
	isOnline: z.boolean(),
	/**
	* ISO-8601 timestamp of last activity
	*
	* Present when isOnline is false to indicate when the user
	* was last seen. Omitted when user is currently online.
	*/
	lastSeen: z.string().datetime().optional()
});
/**
* ACP session update relay
*
* Carries an encrypted ACP session update through the server relay.
* The server treats this as an opaque blob — zero-knowledge relay.
* The app decrypts and parses the inner update using AcpSessionUpdateSchema.
*
* @see HAP-1036 - Adapt happy-server relay and happy-app display for ACP session updates
*/
const ApiEphemeralAcpSessionUpdateSchema = z.object({
	type: z.literal("acp-session-update"),
	/**
	* Session ID — identifies which session the ACP update belongs to
	*
	* @remarks
	* Uses `sid` for consistency with other session-related ephemeral events.
	*
	* @see HAP-654 - Standardization of session ID field names
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/**
	* Encrypted ACP session update payload.
	* Contains the serialized AcpSessionUpdate, encrypted with the session's
	* data encryption key. The server never reads this content.
	*/
	update: z.string().min(1).max(STRING_LIMITS.CONTENT_MAX)
});
/**
* ACP permission request relay
*
* Carries an encrypted ACP permission request from CLI to mobile app.
* The server treats this as an opaque blob — zero-knowledge relay.
* The app decrypts and displays the permission UI for user approval.
*
* @see HAP-1043 - Add ACP remote permission approval flow
*/
const ApiEphemeralAcpPermissionRequestSchema = z.object({
	type: z.literal("acp-permission-request"),
	/**
	* Session ID — identifies which session the permission request belongs to
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/**
	* Unique request ID for correlating the response back to the CLI
	*/
	requestId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/**
	* Encrypted permission request payload.
	* Contains the serialized AcpRequestPermissionRequest, encrypted with the session's
	* data encryption key. The server never reads this content.
	*/
	payload: z.string().min(1).max(STRING_LIMITS.CONTENT_MAX),
	/**
	* Optional timeout in milliseconds from when the request was created.
	* If present, the mobile app should show a countdown and expire the request.
	*/
	timeoutMs: z.number().int().min(0).optional()
});
/**
* ACP session command relay
*
* Carries an encrypted session command from the mobile app to the CLI.
* Commands include: list, load, resume, fork. The server treats the
* payload as an opaque blob -- zero-knowledge relay.
*
* Flow: mobile -> server -> CLI (session-scoped connection)
*
* @see HAP-1072 - Implement ACP session command/response handlers in CLI
*/
const ApiEphemeralAcpSessionCommandSchema = z.object({
	type: z.literal("acp-session-command"),
	/**
	* Session ID -- identifies which session connection receives the command.
	*
	* @remarks
	* Uses `sid` for consistency with other session-related ephemeral events.
	*
	* @see HAP-654 - Standardization of session ID field names
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/**
	* Session command type.
	* Determines how the CLI processes this command.
	*/
	command: z.enum([
		"list",
		"load",
		"resume",
		"fork"
	]),
	/**
	* Encrypted command payload.
	* Contains the serialized command parameters, encrypted with the session's
	* data encryption key. The server never reads this content.
	* For 'list' commands this may be empty/minimal.
	*/
	payload: z.string().max(STRING_LIMITS.CONTENT_MAX),
	/**
	* Unique request ID for correlating the response back to the mobile app.
	*/
	requestId: z.string().min(1).max(STRING_LIMITS.ID_MAX)
});
/**
* ACP session command response relay
*
* Carries an encrypted response to an ACP session command from CLI back to mobile.
* The server treats the payload as an opaque blob -- zero-knowledge relay.
*
* Flow: CLI -> server -> mobile (user-scoped connections)
*
* @see HAP-1072 - Implement ACP session command/response handlers in CLI
*/
const ApiEphemeralAcpSessionCommandResponseSchema = z.object({
	type: z.literal("acp-session-command-response"),
	/**
	* Session ID -- identifies which session the response belongs to.
	*/
	sid: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/**
	* The command this is a response to (mirrors the original command).
	*/
	command: z.enum([
		"list",
		"load",
		"resume",
		"fork"
	]),
	/**
	* Encrypted response payload.
	* Contains the serialized response data, encrypted with the session's
	* data encryption key. The server never reads this content.
	*/
	payload: z.string().min(1).max(STRING_LIMITS.CONTENT_MAX),
	/**
	* Request ID from the original command, for correlation.
	*/
	requestId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/**
	* Whether the command succeeded.
	* When false, the payload contains an error description.
	*/
	success: z.boolean()
});
/**
* Union of all ephemeral update types
*/
const ApiEphemeralUpdateSchema = z.union([
	ApiEphemeralActivityUpdateSchema,
	ApiEphemeralUsageUpdateSchema,
	ApiEphemeralMachineActivityUpdateSchema,
	ApiEphemeralMachineStatusUpdateSchema,
	ApiEphemeralMachineDisconnectedUpdateSchema,
	ApiEphemeralFriendStatusUpdateSchema,
	ApiEphemeralAcpSessionUpdateSchema,
	ApiEphemeralAcpPermissionRequestSchema,
	ApiEphemeralAcpSessionCommandSchema,
	ApiEphemeralAcpSessionCommandResponseSchema
]);

//#endregion
//#region src/payloads.ts
/**
* Payload wrapper schemas
*
* These wrap update and ephemeral events with sequencing metadata.
*
* Security: All string fields have maximum length constraints.
*/
/**
* Update payload container
*
* Wraps update events with sequencing information for ordered delivery.
* The 'body' contains the actual update with 't' field renamed to differentiate
* from the container structure.
*/
const ApiUpdateContainerSchema = z.object({
	id: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	seq: z.number(),
	body: ApiUpdateSchema,
	createdAt: z.number()
});
/**
* Update payload for server-side use
*
* This is the wire format where body.t becomes body.t for the discriminator.
* Matches the format used by eventRouter.ts builder functions.
*/
const UpdatePayloadSchema = z.object({
	id: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	seq: z.number(),
	body: z.object({ t: z.string() }).passthrough().transform((data) => {
		const sanitized = { ...data };
		delete sanitized["__proto__"];
		delete sanitized["constructor"];
		delete sanitized["prototype"];
		return sanitized;
	}),
	createdAt: z.number()
});
/**
* Ephemeral payload wrapper
*
* Simpler than UpdatePayload since ordering isn't critical for ephemeral events.
*/
const EphemeralPayloadSchema = ApiEphemeralUpdateSchema;

//#endregion
//#region src/mcp.ts
/**
* MCP (Model Context Protocol) state schemas for sync
*
* These schemas define the structure for syncing MCP state from CLI to App.
* The CLI tracks MCP server states and can send this information to connected
* mobile apps for display.
*
* Security: All string fields have maximum length constraints.
*/
/**
* Schema for MCP server state sent from CLI to App
*
* This represents the state of a single MCP server as tracked by the CLI.
* The server name is used as the key in the parent record.
*
* @example
* ```typescript
* const serverState = McpServerStateSchema.parse({
*     disabled: false,
*     toolCount: 15,
*     lastValidated: '2024-12-28T10:30:00.000Z',
*     disabledTools: ['dangerous-tool']
* });
* ```
*/
const McpServerStateSchema = z.object({
	disabled: z.boolean(),
	toolCount: z.number().optional(),
	lastValidated: z.string().datetime().max(STRING_LIMITS.LABEL_MAX).optional(),
	disabledTools: z.array(z.string().max(STRING_LIMITS.MCP_TOOL_NAME_MAX)).optional()
});
/**
* Schema for MCP tool info
*
* Minimal information about an MCP tool for display purposes.
*
* @example
* ```typescript
* const tool = McpToolInfoSchema.parse({
*     name: 'search_codebase',
*     description: 'Search for patterns in the codebase'
* });
* ```
*/
const McpToolInfoSchema = z.object({
	name: z.string().min(1).max(STRING_LIMITS.MCP_TOOL_NAME_MAX),
	description: z.string().max(STRING_LIMITS.MCP_TOOL_DESCRIPTION_MAX).optional()
});
/**
* Full MCP state for sync
*
* This is the top-level schema containing all MCP state that gets synced
* from CLI to App. It's designed to be included in session update payloads.
*
* - `servers`: Record of server name -> state (always present)
* - `tools`: Optional record of server name -> tool list (only if tools are fetched)
*
* @example
* ```typescript
* const mcpState = McpSyncStateSchema.parse({
*     servers: {
*         'github-mcp': { disabled: false, toolCount: 10 },
*         'local-tools': { disabled: true }
*     },
*     tools: {
*         'github-mcp': [
*             { name: 'get_issues', description: 'Fetch GitHub issues' },
*             { name: 'create_pr', description: 'Create a pull request' }
*         ]
*     }
* });
* ```
*/
const McpSyncStateSchema = z.object({
	servers: z.record(z.string().max(STRING_LIMITS.MCP_SERVER_NAME_MAX), McpServerStateSchema),
	tools: z.record(z.string().max(STRING_LIMITS.MCP_SERVER_NAME_MAX), z.array(McpToolInfoSchema)).optional()
});

//#endregion
//#region src/helpers.ts
/**
* Type guard: checks if an update contains a session ID
*
* @param update - Any ApiUpdate to check
* @returns `true` if the update contains a session ID
*
* @example
* ```typescript
* const update = parseUpdate(rawData);
* if (hasSessionId(update)) {
*     // TypeScript knows update is SessionIdUpdate here
*     const sessionId = getSessionId(update);
* }
* ```
*/
function hasSessionId(update) {
	return [
		"new-session",
		"update-session",
		"new-message",
		"delete-session"
	].includes(update.t);
}
/**
* Type guard: checks if an ephemeral update contains a session ID
*
* @param update - Any ApiEphemeralUpdate to check
* @returns `true` if the update contains a session ID
*
* @example
* ```typescript
* const ephemeral = parseEphemeral(rawData);
* if (hasSessionIdEphemeral(ephemeral)) {
*     const sessionId = getSessionIdFromEphemeral(ephemeral);
* }
* ```
*/
function hasSessionIdEphemeral(update) {
	return ["activity", "usage"].includes(update.type);
}
/**
* Extract session ID from a persistent update
*
* All session-related schemas now use `sid` for consistency (HAP-654).
*
* @param update - A SessionIdUpdate (use hasSessionId type guard first)
* @returns The session ID string
*
* @example
* ```typescript
* const update = parseUpdate(rawData);
* if (hasSessionId(update)) {
*     const sessionId = getSessionId(update);
*     const encryption = sessionManager.getEncryption(sessionId);
* }
* ```
*/
function getSessionId(update) {
	return update.sid;
}
/**
* Extract session ID from an ephemeral update
*
* All session-related ephemeral schemas now use `sid` for consistency (HAP-654).
*
* @param update - A SessionIdEphemeral (use hasSessionIdEphemeral type guard first)
* @returns The session ID string
*
* @example
* ```typescript
* const ephemeral = parseEphemeral(rawData);
* if (hasSessionIdEphemeral(ephemeral)) {
*     const sessionId = getSessionIdFromEphemeral(ephemeral);
*     updateSessionActivity(sessionId);
* }
* ```
*/
function getSessionIdFromEphemeral(update) {
	return update.sid;
}
/**
* Try to extract session ID from any update
*
* Safe variant that returns `undefined` if the update doesn't contain a session ID,
* instead of throwing an error.
*
* @param update - Any ApiUpdate
* @returns The session ID string, or `undefined` if not present
*
* @example
* ```typescript
* const sessionId = tryGetSessionId(update);
* if (sessionId) {
*     processSession(sessionId);
* } else {
*     // Handle non-session update (e.g., machine update)
* }
* ```
*/
function tryGetSessionId(update) {
	if (hasSessionId(update)) return getSessionId(update);
}
/**
* Try to extract session ID from any ephemeral update
*
* Safe variant that returns `undefined` if the ephemeral update doesn't contain
* a session ID, instead of throwing an error.
*
* @param update - Any ApiEphemeralUpdate
* @returns The session ID string, or `undefined` if not present
*
* @example
* ```typescript
* const sessionId = tryGetSessionIdFromEphemeral(ephemeral);
* if (sessionId) {
*     updateSessionActivity(sessionId);
* }
* ```
*/
function tryGetSessionIdFromEphemeral(update) {
	if (hasSessionIdEphemeral(update)) return getSessionIdFromEphemeral(update);
}
/**
* Type guard: checks if an update contains a machine ID
*
* @param update - Any ApiUpdate to check
* @returns `true` if the update contains a machine ID
*
* @example
* ```typescript
* const update = parseUpdate(rawData);
* if (hasMachineId(update)) {
*     const machineId = getMachineId(update);
* }
* ```
*/
function hasMachineId(update) {
	return ["new-machine", "update-machine"].includes(update.t);
}
/**
* Type guard: checks if an ephemeral update contains a machine ID
*
* @param update - Any ApiEphemeralUpdate to check
* @returns `true` if the update contains a machine ID
*
* @example
* ```typescript
* const ephemeral = parseEphemeral(rawData);
* if (hasMachineIdEphemeral(ephemeral)) {
*     const machineId = getMachineIdFromEphemeral(ephemeral);
* }
* ```
*/
function hasMachineIdEphemeral(update) {
	return [
		"machine-activity",
		"machine-status",
		"machine-disconnected"
	].includes(update.type);
}
/**
* Extract machine ID from a persistent update
*
* Both `new-machine` and `update-machine` use the `machineId` field.
*
* @param update - A MachineIdUpdate (use hasMachineId type guard first)
* @returns The machine ID string
*
* @example
* ```typescript
* const update = parseUpdate(rawData);
* if (hasMachineId(update)) {
*     const machineId = getMachineId(update);
*     updateMachineState(machineId);
* }
* ```
*/
function getMachineId(update) {
	switch (update.t) {
		case "new-machine":
		case "update-machine": return update.machineId;
		default: throw new Error(`Unknown update type: ${update.t}`);
	}
}
/**
* Extract machine ID from an ephemeral update
*
* All machine-related ephemeral schemas now use `machineId` for consistency (HAP-655).
*
* @param update - A MachineIdEphemeral (use hasMachineIdEphemeral type guard first)
* @returns The machine ID string
*
* @example
* ```typescript
* const ephemeral = parseEphemeral(rawData);
* if (hasMachineIdEphemeral(ephemeral)) {
*     const machineId = getMachineIdFromEphemeral(ephemeral);
*     updateMachineStatus(machineId);
* }
* ```
*/
function getMachineIdFromEphemeral(update) {
	return update.machineId;
}
/**
* Try to extract machine ID from any update
*
* Safe variant that returns `undefined` if the update doesn't contain a machine ID,
* instead of throwing an error.
*
* @param update - Any ApiUpdate
* @returns The machine ID string, or `undefined` if not present
*
* @example
* ```typescript
* const machineId = tryGetMachineId(update);
* if (machineId) {
*     processMachine(machineId);
* }
* ```
*/
function tryGetMachineId(update) {
	if (hasMachineId(update)) return getMachineId(update);
}
/**
* Try to extract machine ID from any ephemeral update
*
* Safe variant that returns `undefined` if the ephemeral update doesn't contain
* a machine ID, instead of throwing an error.
*
* @param update - Any ApiEphemeralUpdate
* @returns The machine ID string, or `undefined` if not present
*
* @example
* ```typescript
* const machineId = tryGetMachineIdFromEphemeral(ephemeral);
* if (machineId) {
*     updateMachineActivity(machineId);
* }
* ```
*/
function tryGetMachineIdFromEphemeral(update) {
	if (hasMachineIdEphemeral(update)) return getMachineIdFromEphemeral(update);
}

//#endregion
//#region src/usageLimits.ts
/**
* Usage limits schemas for plan and rate limiting data
*
* These schemas define the structure for usage limit information
* returned by the API to help users understand their plan constraints.
*
* @example
* ```typescript
* import { PlanLimitsResponseSchema, type PlanLimitsResponse } from '@happy/protocol';
*
* const result = PlanLimitsResponseSchema.safeParse(data);
* if (result.success) {
*     const limits: PlanLimitsResponse = result.data;
*     console.log('Session limit:', limits.sessionLimit?.percentageUsed);
* }
* ```
*/
/**
* Single usage limit entry
*
* Represents one limit category (e.g., tokens, requests, sessions)
* with percentage used and reset information.
*
* @example
* ```typescript
* const limit = UsageLimitSchema.parse({
*     id: 'opus_tokens',
*     label: 'Opus Tokens',
*     percentageUsed: 75.5,
*     resetsAt: 1735689600000,
*     resetDisplayType: 'countdown',
*     description: 'Monthly Opus token limit',
* });
* ```
*/
const UsageLimitSchema = z.object({
	/** Unique identifier for this limit type */
	id: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/** Human-readable label for display */
	label: z.string().min(1).max(STRING_LIMITS.LABEL_MAX),
	/** Percentage of limit used (0-100) */
	percentageUsed: z.number().min(0).max(100),
	/** Unix timestamp (ms) when this limit resets, null if no reset */
	resetsAt: z.number().nullable(),
	/** How to display the reset time in UI */
	resetDisplayType: z.enum(["countdown", "datetime"]),
	/** Optional description for additional context */
	description: z.string().max(STRING_LIMITS.DESCRIPTION_MAX).optional()
});
/**
* Full plan limits response from API
*
* Contains session limit (if applicable), weekly limits array,
* and metadata about availability.
*
* @example
* ```typescript
* const response = PlanLimitsResponseSchema.parse({
*     sessionLimit: {
*         id: 'session',
*         label: 'Session Limit',
*         percentageUsed: 50,
*         resetsAt: null,
*         resetDisplayType: 'datetime',
*     },
*     weeklyLimits: [
*         {
*             id: 'opus_tokens',
*             label: 'Opus Tokens',
*             percentageUsed: 25,
*             resetsAt: 1735689600000,
*             resetDisplayType: 'countdown',
*         },
*     ],
*     lastUpdatedAt: Date.now(),
*     limitsAvailable: true,
*     provider: 'anthropic',
* });
* ```
*/
const PlanLimitsResponseSchema = z.object({
	/** Session limit if applicable (e.g., concurrent session count) */
	sessionLimit: UsageLimitSchema.optional(),
	/** Weekly/rolling limits (tokens, requests, etc.) */
	weeklyLimits: z.array(UsageLimitSchema),
	/** Unix timestamp (ms) when limits were last fetched/updated */
	lastUpdatedAt: z.number(),
	/** Whether limit information is available from the provider */
	limitsAvailable: z.boolean(),
	/** Optional provider identifier (e.g., 'anthropic', 'openai') */
	provider: z.string().max(STRING_LIMITS.LABEL_MAX).optional()
});

//#endregion
//#region src/sharing.ts
/**
* Session sharing schemas for sharing sessions with other users
*
* These schemas define the structure for session sharing permissions,
* URL sharing configuration, and email invitations.
*
* @example
* ```typescript
* import { SessionShareSettingsSchema, type SessionShareSettings } from '@happy/protocol';
*
* const result = SessionShareSettingsSchema.safeParse(data);
* if (result.success) {
*     const settings: SessionShareSettings = result.data;
*     console.log('Shares:', settings.shares.length);
* }
* ```
*/
/**
* Permission levels for session sharing
*
* @example
* ```typescript
* const permission = SessionSharePermissionSchema.parse('view_only');
* // Valid values: 'view_only', 'view_and_chat'
* ```
*/
const SessionSharePermissionSchema = z.enum(["view_only", "view_and_chat"]);
/**
* Individual session share entry representing a user who has access
*
* @example
* ```typescript
* const entry = SessionShareEntrySchema.parse({
*     id: '550e8400-e29b-41d4-a716-446655440000',
*     userId: 'user_abc123',
*     permission: 'view_and_chat',
*     sharedAt: '2026-01-04T12:00:00.000Z',
*     sharedBy: 'user_xyz789',
* });
* ```
*/
const SessionShareEntrySchema = z.object({
	/** Unique identifier for this share entry */
	id: z.string().uuid(),
	/** User ID of the person who has access */
	userId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/** Optional user profile information */
	userProfile: UserProfileSchema.optional(),
	/** Permission level granted */
	permission: SessionSharePermissionSchema,
	/** ISO 8601 timestamp when this share was created */
	sharedAt: z.string().datetime(),
	/** User ID of the person who granted access */
	sharedBy: z.string().min(1).max(STRING_LIMITS.ID_MAX)
});
/**
* URL sharing configuration for public link access
*
* @example
* ```typescript
* const urlConfig = SessionShareUrlConfigSchema.parse({
*     enabled: true,
*     token: 'abc123xyz789',
*     permission: 'view_only',
* });
* ```
*/
const SessionShareUrlConfigSchema = z.object({
	/** Whether URL sharing is enabled */
	enabled: z.boolean(),
	/** Unique token for the shareable URL (generated server-side) */
	token: z.string().max(STRING_LIMITS.TOKEN_MAX).optional(),
	/** Optional password protection for the shared URL */
	password: z.string().max(STRING_LIMITS.NAME_MAX).optional(),
	/** Permission level for users accessing via URL */
	permission: SessionSharePermissionSchema,
	/** Optional ISO 8601 expiration timestamp */
	expiresAt: z.string().datetime().optional()
});
/**
* Invitation status for email-based invitations
*
* @example
* ```typescript
* const status = InvitationStatusSchema.parse('pending');
* // Valid values: 'pending', 'accepted', 'expired', 'revoked'
* ```
*/
const InvitationStatusSchema = z.enum([
	"pending",
	"accepted",
	"expired",
	"revoked"
]);
/**
* Email invitation for non-users
*
* @example
* ```typescript
* const invitation = SessionShareInvitationSchema.parse({
*     id: '550e8400-e29b-41d4-a716-446655440000',
*     email: 'friend@example.com',
*     permission: 'view_only',
*     invitedAt: '2026-01-04T12:00:00.000Z',
*     invitedBy: 'user_xyz789',
*     status: 'pending',
*     expiresAt: '2026-01-11T12:00:00.000Z',
* });
* ```
*/
const SessionShareInvitationSchema = z.object({
	/** Unique identifier for this invitation */
	id: z.string().uuid(),
	/** Email address of the invitee */
	email: z.string().email().max(STRING_LIMITS.NAME_MAX),
	/** Permission level to be granted when accepted */
	permission: SessionSharePermissionSchema,
	/** ISO 8601 timestamp when invitation was sent */
	invitedAt: z.string().datetime(),
	/** User ID of the person who sent the invitation */
	invitedBy: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/** Current status of the invitation */
	status: InvitationStatusSchema,
	/** ISO 8601 timestamp when invitation expires */
	expiresAt: z.string().datetime()
});
/**
* Complete session sharing settings
*
* @example
* ```typescript
* const settings = SessionShareSettingsSchema.parse({
*     sessionId: 'session_abc123',
*     shares: [],
*     urlSharing: {
*         enabled: false,
*         permission: 'view_only',
*     },
*     invitations: [],
* });
* ```
*/
const SessionShareSettingsSchema = z.object({
	/** Session ID these settings apply to */
	sessionId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/** List of users who have access */
	shares: z.array(SessionShareEntrySchema),
	/** URL sharing configuration */
	urlSharing: SessionShareUrlConfigSchema,
	/** Pending email invitations */
	invitations: z.array(SessionShareInvitationSchema)
});
/**
* Request to add a session share (either by userId or email)
*
* At least one of userId or email must be provided.
*
* @example
* ```typescript
* // Share with existing user
* const byUser = AddSessionShareRequestSchema.parse({
*     sessionId: 'session_abc123',
*     userId: 'user_xyz789',
*     permission: 'view_and_chat',
* });
*
* // Invite via email
* const byEmail = AddSessionShareRequestSchema.parse({
*     sessionId: 'session_abc123',
*     email: 'friend@example.com',
*     permission: 'view_only',
* });
* ```
*/
const AddSessionShareRequestSchema = z.object({
	/** Session ID to share */
	sessionId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/** User ID to share with (for existing users) */
	userId: z.string().min(1).max(STRING_LIMITS.ID_MAX).optional(),
	/** Email to invite (for non-users) */
	email: z.string().email().max(STRING_LIMITS.NAME_MAX).optional(),
	/** Permission level to grant */
	permission: SessionSharePermissionSchema
}).refine((data) => data.userId || data.email, { message: "Either userId or email must be provided" });
/**
* Request to update an existing share's permission level
*
* @example
* ```typescript
* const request = UpdateSessionShareRequestSchema.parse({
*     shareId: '550e8400-e29b-41d4-a716-446655440000',
*     permission: 'view_only',
* });
* ```
*/
const UpdateSessionShareRequestSchema = z.object({
	/** Share entry ID to update */
	shareId: z.string().uuid(),
	/** New permission level */
	permission: SessionSharePermissionSchema
});
/**
* Request to remove a session share
*
* @example
* ```typescript
* const request = RemoveSessionShareRequestSchema.parse({
*     shareId: '550e8400-e29b-41d4-a716-446655440000',
* });
* ```
*/
const RemoveSessionShareRequestSchema = z.object({ 
/** Share entry ID to remove */
shareId: z.string().uuid() });
/**
* Request to update URL sharing configuration
*
* @example
* ```typescript
* // Enable URL sharing with password
* const request = UpdateUrlSharingRequestSchema.parse({
*     sessionId: 'session_abc123',
*     enabled: true,
*     password: 'secret123',
*     permission: 'view_only',
* });
* ```
*/
const UpdateUrlSharingRequestSchema = z.object({
	/** Session ID to update URL sharing for */
	sessionId: z.string().min(1).max(STRING_LIMITS.ID_MAX),
	/** Whether to enable or disable URL sharing */
	enabled: z.boolean(),
	/** Optional password protection (pass null to remove) */
	password: z.string().max(STRING_LIMITS.NAME_MAX).nullable().optional(),
	/** Optional new permission level for URL access */
	permission: SessionSharePermissionSchema.optional()
});
/**
* Request to revoke an email invitation
*
* @example
* ```typescript
* const request = RevokeInvitationRequestSchema.parse({
*     invitationId: '550e8400-e29b-41d4-a716-446655440000',
* });
* ```
*/
const RevokeInvitationRequestSchema = z.object({ 
/** Invitation ID to revoke */
invitationId: z.string().uuid() });
/**
* Request to resend an email invitation
*
* @example
* ```typescript
* const request = ResendInvitationRequestSchema.parse({
*     invitationId: '550e8400-e29b-41d4-a716-446655440000',
* });
* ```
*/
const ResendInvitationRequestSchema = z.object({ 
/** Invitation ID to resend */
invitationId: z.string().uuid() });

//#endregion
//#region src/acp/jsonrpc.ts
/**
* JSON-RPC 2.0 base types for the Agent Client Protocol
*
* These schemas define the foundational message structure used by ACP.
* All ACP communication follows JSON-RPC 2.0 with optional `_meta` extensions.
*
* @see https://www.jsonrpc.org/specification
* @see https://agentclientprotocol.com/protocol/schema
*/
/**
* JSON-RPC request ID
*
* An identifier established by the client. Must contain a String, Number, or NULL value.
* The server MUST reply with the same value in the Response object.
*/
const AcpRequestIdSchema = z.union([
	z.null(),
	z.number().int(),
	z.string()
]);
/**
* ACP extensibility metadata
*
* Reserved by ACP for client and agent metadata. Implementations MUST NOT
* make assumptions about values at these keys.
*
* @see https://agentclientprotocol.com/protocol/extensibility
*/
const AcpMetaSchema = z.record(z.string(), z.unknown()).nullable().optional();
/**
* Predefined JSON-RPC and ACP-specific error codes
*
* Standard JSON-RPC codes plus ACP-specific codes in the reserved range.
*/
const AcpErrorCodeSchema = z.number().int();
/** Well-known error code constants */
const ACP_ERROR_CODES = {
	/** Invalid JSON was received */
	PARSE_ERROR: -32700,
	/** The JSON sent is not a valid Request object */
	INVALID_REQUEST: -32600,
	/** The method does not exist or is not available */
	METHOD_NOT_FOUND: -32601,
	/** Invalid method parameter(s) */
	INVALID_PARAMS: -32602,
	/** Internal JSON-RPC error */
	INTERNAL_ERROR: -32603,
	/** Request cancelled (UNSTABLE) */
	REQUEST_CANCELLED: -32800,
	/** Authentication is required before this operation */
	AUTH_REQUIRED: -32e3,
	/** A given resource was not found */
	RESOURCE_NOT_FOUND: -32002
};
/**
* JSON-RPC error object
*
* Represents an error that occurred during method execution.
*
* @see https://www.jsonrpc.org/specification#error_object
*/
const AcpErrorSchema = z.object({
	code: AcpErrorCodeSchema,
	message: z.string(),
	data: z.unknown().optional()
});
/**
* JSON-RPC 2.0 request
*/
const AcpJsonRpcRequestSchema = z.object({
	jsonrpc: z.literal("2.0").optional(),
	id: AcpRequestIdSchema,
	method: z.string(),
	params: z.unknown().optional()
});
/**
* JSON-RPC 2.0 successful response
*/
const AcpJsonRpcResponseSchema = z.object({
	jsonrpc: z.literal("2.0").optional(),
	id: AcpRequestIdSchema,
	result: z.unknown()
});
/**
* JSON-RPC 2.0 error response
*/
const AcpJsonRpcErrorResponseSchema = z.object({
	jsonrpc: z.literal("2.0").optional(),
	id: AcpRequestIdSchema,
	error: AcpErrorSchema
});
/**
* JSON-RPC 2.0 notification (no id, no response expected)
*/
const AcpJsonRpcNotificationSchema = z.object({
	jsonrpc: z.literal("2.0").optional(),
	method: z.string(),
	params: z.unknown().optional()
});

//#endregion
//#region src/acp/common.ts
/**
* Shared ACP types used across multiple schema modules
*
* These are foundational types referenced by initialization, sessions,
* updates, tool calls, and resource schemas.
*
* @see https://agentclientprotocol.com/protocol/schema
*/
/** Unique identifier for a conversation session */
const AcpSessionIdSchema = z.string();
/** Unique identifier for a tool call within a session */
const AcpToolCallIdSchema = z.string();
/** Unique identifier for a session mode */
const AcpSessionModeIdSchema = z.string();
/** Unique identifier for a session configuration option */
const AcpSessionConfigIdSchema = z.string();
/** Unique identifier for a session configuration option value */
const AcpSessionConfigValueIdSchema = z.string();
/** Unique identifier for a session configuration option value group */
const AcpSessionConfigGroupIdSchema = z.string();
/** Unique identifier for a permission option */
const AcpPermissionOptionIdSchema = z.string();
/** Unique identifier for a model (UNSTABLE) */
const AcpModelIdSchema = z.string();
/**
* Protocol version identifier
*
* Only bumped for breaking changes. Non-breaking changes use capabilities.
* Unsigned 16-bit integer (0-65535).
*/
const AcpProtocolVersionSchema = z.number().int().min(0).max(65535);
/**
* Metadata about the implementation of the client or agent
*
* Describes the name and version with an optional title for UI representation.
*/
const AcpImplementationSchema = z.object({
	_meta: AcpMetaSchema,
	name: z.string(),
	title: z.string().nullable().optional(),
	version: z.string()
});
/** An environment variable for MCP server or terminal processes */
const AcpEnvVariableSchema = z.object({
	_meta: AcpMetaSchema,
	name: z.string(),
	value: z.string()
});
/** An HTTP header for MCP server connections */
const AcpHttpHeaderSchema = z.object({
	_meta: AcpMetaSchema,
	name: z.string(),
	value: z.string()
});
/** Role in a conversation */
const AcpRoleSchema = z.enum(["assistant", "user"]);
/**
* Optional annotations for the client
*
* Used to inform how objects are used or displayed.
*/
const AcpAnnotationsSchema = z.object({
	_meta: AcpMetaSchema,
	audience: z.array(AcpRoleSchema).nullable().optional(),
	lastModified: z.string().nullable().optional(),
	priority: z.number().nullable().optional()
});
/** Text-based resource contents */
const AcpTextResourceContentsSchema = z.object({
	_meta: AcpMetaSchema,
	mimeType: z.string().nullable().optional(),
	text: z.string(),
	uri: z.string()
});
/** Binary resource contents (base64 encoded) */
const AcpBlobResourceContentsSchema = z.object({
	_meta: AcpMetaSchema,
	blob: z.string(),
	mimeType: z.string().nullable().optional(),
	uri: z.string()
});
/** Resource content that can be embedded in a message */
const AcpEmbeddedResourceResourceSchema = z.union([AcpTextResourceContentsSchema, AcpBlobResourceContentsSchema]);
/** Text content block */
const AcpTextContentSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("text"),
	annotations: AcpAnnotationsSchema.nullable().optional(),
	text: z.string()
});
/** Image content block */
const AcpImageContentSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("image"),
	annotations: AcpAnnotationsSchema.nullable().optional(),
	data: z.string(),
	mimeType: z.string(),
	uri: z.string().nullable().optional()
});
/** Audio content block */
const AcpAudioContentSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("audio"),
	annotations: AcpAnnotationsSchema.nullable().optional(),
	data: z.string(),
	mimeType: z.string()
});
/** Resource link content block */
const AcpResourceLinkSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("resource_link"),
	annotations: AcpAnnotationsSchema.nullable().optional(),
	description: z.string().nullable().optional(),
	mimeType: z.string().nullable().optional(),
	name: z.string(),
	size: z.number().int().nullable().optional(),
	title: z.string().nullable().optional(),
	uri: z.string()
});
/** Embedded resource content block */
const AcpEmbeddedResourceSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("resource"),
	annotations: AcpAnnotationsSchema.nullable().optional(),
	resource: AcpEmbeddedResourceResourceSchema
});
/**
* Content block - discriminated union on `type` field
*
* Content blocks represent displayable information in the Agent Client Protocol.
* They appear in user prompts, agent output, and tool call results.
*
* Discriminated on `type`: "text" | "image" | "audio" | "resource_link" | "resource"
*
* @see https://agentclientprotocol.com/protocol/content
*/
const AcpContentBlockSchema = z.discriminatedUnion("type", [
	AcpTextContentSchema,
	AcpImageContentSchema,
	AcpAudioContentSchema,
	AcpResourceLinkSchema,
	AcpEmbeddedResourceSchema
]);
/**
* A mode the agent can operate in
*
* @see https://agentclientprotocol.com/protocol/session-modes
*/
const AcpSessionModeSchema = z.object({
	_meta: AcpMetaSchema,
	id: AcpSessionModeIdSchema,
	name: z.string(),
	description: z.string().nullable().optional()
});
/** The set of modes and the one currently active */
const AcpSessionModeStateSchema = z.object({
	_meta: AcpMetaSchema,
	availableModes: z.array(AcpSessionModeSchema),
	currentModeId: AcpSessionModeIdSchema
});
/** A possible value for a session configuration option */
const AcpSessionConfigSelectOptionSchema = z.object({
	_meta: AcpMetaSchema,
	description: z.string().nullable().optional(),
	name: z.string(),
	value: AcpSessionConfigValueIdSchema
});
/** A group of possible values for a session configuration option */
const AcpSessionConfigSelectGroupSchema = z.object({
	_meta: AcpMetaSchema,
	group: AcpSessionConfigGroupIdSchema,
	name: z.string(),
	options: z.array(AcpSessionConfigSelectOptionSchema)
});
/** Possible values for a session configuration option (flat or grouped) */
const AcpSessionConfigSelectOptionsSchema = z.union([z.array(AcpSessionConfigSelectOptionSchema), z.array(AcpSessionConfigSelectGroupSchema)]);
/** Semantic category for a session configuration option */
const AcpSessionConfigOptionCategorySchema = z.string();
/**
* A session configuration option selector and its current state
*
* Discriminated on `type`: currently only "select" is defined.
*
* @see https://agentclientprotocol.com/protocol/schema
*/
const AcpSessionConfigOptionSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("select"),
	id: AcpSessionConfigIdSchema,
	name: z.string(),
	description: z.string().nullable().optional(),
	category: AcpSessionConfigOptionCategorySchema.nullable().optional(),
	currentValue: AcpSessionConfigValueIdSchema,
	options: AcpSessionConfigSelectOptionsSchema
});
/** Information about a selectable model (UNSTABLE) */
const AcpModelInfoSchema = z.object({
	_meta: AcpMetaSchema,
	modelId: AcpModelIdSchema,
	name: z.string(),
	description: z.string().nullable().optional()
});
/** The set of models and the one currently active (UNSTABLE) */
const AcpSessionModelStateSchema = z.object({
	_meta: AcpMetaSchema,
	availableModels: z.array(AcpModelInfoSchema),
	currentModelId: AcpModelIdSchema
});
/** Information about a session returned by session/list (UNSTABLE) */
const AcpSessionInfoSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	cwd: z.string(),
	title: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional()
});
/** Stdio transport configuration for MCP */
const AcpMcpServerStdioSchema = z.object({
	_meta: AcpMetaSchema,
	name: z.string(),
	command: z.string(),
	args: z.array(z.string()),
	env: z.array(AcpEnvVariableSchema)
});
/** HTTP transport configuration for MCP */
const AcpMcpServerHttpSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("http"),
	name: z.string(),
	url: z.string(),
	headers: z.array(AcpHttpHeaderSchema)
});
/** SSE transport configuration for MCP */
const AcpMcpServerSseSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("sse"),
	name: z.string(),
	url: z.string(),
	headers: z.array(AcpHttpHeaderSchema)
});
/**
* Configuration for connecting to an MCP server
*
* The schema uses `type` as discriminator for http/sse variants.
* The stdio variant does not include a `type` field (it is the default transport).
*
* @see https://agentclientprotocol.com/protocol/session-setup#mcp-servers
*/
const AcpMcpServerSchema = z.union([
	AcpMcpServerHttpSchema,
	AcpMcpServerSseSchema,
	AcpMcpServerStdioSchema
]);

//#endregion
//#region src/acp/capabilities.ts
/**
* ACP capability negotiation types
*
* Capabilities are exchanged during initialization to determine which
* features are available for the connection.
*
* @see https://agentclientprotocol.com/protocol/initialization
*/
/**
* File system capabilities supported by the client
*
* @see https://agentclientprotocol.com/protocol/initialization#filesystem
*/
const AcpFileSystemCapabilitySchema = z.object({
	_meta: AcpMetaSchema,
	readTextFile: z.boolean().default(false),
	writeTextFile: z.boolean().default(false)
});
/**
* Capabilities supported by the client
*
* Advertised during initialization to inform the agent about available features.
*
* @see https://agentclientprotocol.com/protocol/initialization#client-capabilities
*/
const AcpClientCapabilitiesSchema = z.object({
	_meta: AcpMetaSchema,
	fs: AcpFileSystemCapabilitySchema.default({
		readTextFile: false,
		writeTextFile: false
	}),
	terminal: z.boolean().default(false)
});
/** MCP capabilities supported by the agent */
const AcpMcpCapabilitiesSchema = z.object({
	_meta: AcpMetaSchema,
	http: z.boolean().default(false),
	sse: z.boolean().default(false)
});
/**
* Prompt capabilities supported by the agent
*
* Baseline support requires text and resource_link. Other types must be opted in.
*
* @see https://agentclientprotocol.com/protocol/initialization#prompt-capabilities
*/
const AcpPromptCapabilitiesSchema = z.object({
	_meta: AcpMetaSchema,
	audio: z.boolean().default(false),
	embeddedContext: z.boolean().default(false),
	image: z.boolean().default(false)
});
/** Capabilities for session/fork (UNSTABLE) */
const AcpSessionForkCapabilitiesSchema = z.object({ _meta: AcpMetaSchema });
/** Capabilities for session/list (UNSTABLE) */
const AcpSessionListCapabilitiesSchema = z.object({ _meta: AcpMetaSchema });
/** Capabilities for session/resume (UNSTABLE) */
const AcpSessionResumeCapabilitiesSchema = z.object({ _meta: AcpMetaSchema });
/**
* Session capabilities supported by the agent
*
* @see https://agentclientprotocol.com/protocol/initialization#session-capabilities
*/
const AcpSessionCapabilitiesSchema = z.object({
	_meta: AcpMetaSchema,
	fork: AcpSessionForkCapabilitiesSchema.nullable().optional(),
	list: AcpSessionListCapabilitiesSchema.nullable().optional(),
	resume: AcpSessionResumeCapabilitiesSchema.nullable().optional()
});
/**
* Capabilities supported by the agent
*
* Advertised during initialization to inform the client about available features.
*
* @see https://agentclientprotocol.com/protocol/initialization#agent-capabilities
*/
const AcpAgentCapabilitiesSchema = z.object({
	_meta: AcpMetaSchema,
	loadSession: z.boolean().default(false),
	mcpCapabilities: AcpMcpCapabilitiesSchema.default({
		http: false,
		sse: false
	}),
	promptCapabilities: AcpPromptCapabilitiesSchema.default({
		audio: false,
		embeddedContext: false,
		image: false
	}),
	sessionCapabilities: AcpSessionCapabilitiesSchema.default({})
});

//#endregion
//#region src/acp/initialization.ts
/**
* ACP initialization and authentication types
*
* These schemas define the connection handshake between client and agent,
* including capability negotiation and authentication.
*
* @see https://agentclientprotocol.com/protocol/initialization
*/
/**
* Describes an available authentication method
*
* Agents advertise auth methods in the initialize response.
*/
const AcpAuthMethodSchema = z.object({
	_meta: AcpMetaSchema,
	id: z.string(),
	name: z.string(),
	description: z.string().nullable().optional()
});
/**
* Request parameters for the authenticate method
*
* Specifies which authentication method to use.
*/
const AcpAuthenticateRequestSchema = z.object({
	_meta: AcpMetaSchema,
	methodId: z.string()
});
/** Response to the authenticate method */
const AcpAuthenticateResponseSchema = z.object({ _meta: AcpMetaSchema });
/**
* Request parameters for the initialize method
*
* Sent by the client to establish connection and negotiate capabilities.
*
* @see https://agentclientprotocol.com/protocol/initialization
*/
const AcpInitializeRequestSchema = z.object({
	_meta: AcpMetaSchema,
	clientCapabilities: AcpClientCapabilitiesSchema.default({
		fs: {
			readTextFile: false,
			writeTextFile: false
		},
		terminal: false
	}),
	clientInfo: AcpImplementationSchema.nullable().optional(),
	protocolVersion: AcpProtocolVersionSchema
});
/**
* Response to the initialize method
*
* Contains the negotiated protocol version and agent capabilities.
*
* @see https://agentclientprotocol.com/protocol/initialization
*/
const AcpInitializeResponseSchema = z.object({
	_meta: AcpMetaSchema,
	agentCapabilities: AcpAgentCapabilitiesSchema.default({
		loadSession: false,
		mcpCapabilities: {
			http: false,
			sse: false
		},
		promptCapabilities: {
			audio: false,
			embeddedContext: false,
			image: false
		},
		sessionCapabilities: {}
	}),
	agentInfo: AcpImplementationSchema.nullable().optional(),
	authMethods: z.array(AcpAuthMethodSchema).default([]),
	protocolVersion: AcpProtocolVersionSchema
});

//#endregion
//#region src/acp/sessions.ts
/**
* ACP session management types
*
* These schemas define session lifecycle operations: creation, loading,
* resuming, forking, listing, mode switching, and model selection.
*
* @see https://agentclientprotocol.com/protocol/session-setup
*/
/**
* Request parameters for creating a new session
*
* @see https://agentclientprotocol.com/protocol/session-setup#creating-a-session
*/
const AcpNewSessionRequestSchema = z.object({
	_meta: AcpMetaSchema,
	cwd: z.string(),
	mcpServers: z.array(AcpMcpServerSchema)
});
/**
* Response from creating a new session
*
* @see https://agentclientprotocol.com/protocol/session-setup#creating-a-session
*/
const AcpNewSessionResponseSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	configOptions: z.array(AcpSessionConfigOptionSchema).nullable().optional(),
	modes: AcpSessionModeStateSchema.nullable().optional(),
	models: AcpSessionModelStateSchema.nullable().optional()
});
/**
* Request parameters for loading an existing session
*
* Only available if the agent advertises the `loadSession` capability.
*
* @see https://agentclientprotocol.com/protocol/session-setup#loading-sessions
*/
const AcpLoadSessionRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	cwd: z.string(),
	mcpServers: z.array(AcpMcpServerSchema)
});
/** Response from loading an existing session */
const AcpLoadSessionResponseSchema = z.object({
	_meta: AcpMetaSchema,
	configOptions: z.array(AcpSessionConfigOptionSchema).nullable().optional(),
	modes: AcpSessionModeStateSchema.nullable().optional(),
	models: AcpSessionModelStateSchema.nullable().optional()
});
/**
* Request parameters for resuming an existing session (UNSTABLE)
*
* Resumes without returning previous messages (unlike session/load).
* Only available if the agent supports the `session.resume` capability.
*/
const AcpResumeSessionRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	cwd: z.string(),
	mcpServers: z.array(AcpMcpServerSchema).optional()
});
/** Response from resuming an existing session (UNSTABLE) */
const AcpResumeSessionResponseSchema = z.object({
	_meta: AcpMetaSchema,
	configOptions: z.array(AcpSessionConfigOptionSchema).nullable().optional(),
	modes: AcpSessionModeStateSchema.nullable().optional(),
	models: AcpSessionModelStateSchema.nullable().optional()
});
/**
* Request parameters for forking an existing session (UNSTABLE)
*
* Creates a new session based on the context of an existing one.
* Only available if the agent supports the `session.fork` capability.
*/
const AcpForkSessionRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	cwd: z.string(),
	mcpServers: z.array(AcpMcpServerSchema).optional()
});
/** Response from forking an existing session (UNSTABLE) */
const AcpForkSessionResponseSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	configOptions: z.array(AcpSessionConfigOptionSchema).nullable().optional(),
	modes: AcpSessionModeStateSchema.nullable().optional(),
	models: AcpSessionModelStateSchema.nullable().optional()
});
/**
* Request parameters for listing existing sessions (UNSTABLE)
*
* Only available if the agent supports the `listSessions` capability.
*/
const AcpListSessionsRequestSchema = z.object({
	_meta: AcpMetaSchema,
	cursor: z.string().nullable().optional(),
	cwd: z.string().nullable().optional()
});
/** Response from listing sessions (UNSTABLE) */
const AcpListSessionsResponseSchema = z.object({
	_meta: AcpMetaSchema,
	sessions: z.array(AcpSessionInfoSchema),
	nextCursor: z.string().nullable().optional()
});
/**
* Request parameters for setting a session mode
*
* @see https://agentclientprotocol.com/protocol/session-modes
*/
const AcpSetSessionModeRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	modeId: AcpSessionModeIdSchema
});
/** Response to session/set_mode */
const AcpSetSessionModeResponseSchema = z.object({ _meta: AcpMetaSchema });
/** Request parameters for setting a session configuration option */
const AcpSetSessionConfigOptionRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	configId: AcpSessionConfigIdSchema,
	value: AcpSessionConfigValueIdSchema
});
/** Response to session/set_config_option */
const AcpSetSessionConfigOptionResponseSchema = z.object({
	_meta: AcpMetaSchema,
	configOptions: z.array(AcpSessionConfigOptionSchema)
});
/** Request parameters for setting a session model (UNSTABLE) */
const AcpSetSessionModelRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	modelId: AcpModelIdSchema
});
/** Response to session/set_model (UNSTABLE) */
const AcpSetSessionModelResponseSchema = z.object({ _meta: AcpMetaSchema });

//#endregion
//#region src/acp/toolcalls.ts
/**
* ACP tool call and permission types
*
* These schemas define tool call lifecycle, content, locations,
* and the permission request/response flow.
*
* @see https://agentclientprotocol.com/protocol/tool-calls
*/
/**
* Categories of tools that can be invoked
*
* Helps clients choose appropriate icons and UI treatment.
*
* @see https://agentclientprotocol.com/protocol/tool-calls#creating
*/
const AcpToolKindSchema = z.enum([
	"read",
	"edit",
	"delete",
	"move",
	"search",
	"execute",
	"think",
	"fetch",
	"switch_mode",
	"other"
]);
/**
* Execution status of a tool call
*
* @see https://agentclientprotocol.com/protocol/tool-calls#status
*/
const AcpToolCallStatusSchema = z.enum([
	"pending",
	"in_progress",
	"completed",
	"failed"
]);
/**
* A file location being accessed or modified by a tool
*
* Enables "follow-along" features in clients.
*
* @see https://agentclientprotocol.com/protocol/tool-calls#following-the-agent
*/
const AcpToolCallLocationSchema = z.object({
	_meta: AcpMetaSchema,
	path: z.string(),
	line: z.number().int().min(0).nullable().optional()
});
/** Standard content within a tool call */
const AcpToolCallContentContentSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("content"),
	content: AcpContentBlockSchema
});
/** Diff content showing file modifications */
const AcpToolCallContentDiffSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("diff"),
	path: z.string(),
	newText: z.string(),
	oldText: z.string().nullable().optional()
});
/** Terminal content embedding a created terminal */
const AcpToolCallContentTerminalSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("terminal"),
	terminalId: z.string()
});
/**
* Content produced by a tool call - discriminated union on `type`
*
* Discriminated on `type`: "content" | "diff" | "terminal"
*
* @see https://agentclientprotocol.com/protocol/tool-calls#content
*/
const AcpToolCallContentSchema = z.discriminatedUnion("type", [
	AcpToolCallContentContentSchema,
	AcpToolCallContentDiffSchema,
	AcpToolCallContentTerminalSchema
]);
/**
* Represents a tool call that the language model has requested
*
* @see https://agentclientprotocol.com/protocol/tool-calls
*/
const AcpToolCallSchema = z.object({
	_meta: AcpMetaSchema,
	toolCallId: AcpToolCallIdSchema,
	title: z.string(),
	kind: AcpToolKindSchema.optional(),
	status: AcpToolCallStatusSchema.optional(),
	rawInput: z.unknown().optional(),
	rawOutput: z.unknown().optional(),
	content: z.array(AcpToolCallContentSchema).optional(),
	locations: z.array(AcpToolCallLocationSchema).optional()
});
/**
* An update to an existing tool call
*
* All fields except toolCallId are optional - only changed fields need to be included.
*
* @see https://agentclientprotocol.com/protocol/tool-calls#updating
*/
const AcpToolCallUpdateSchema = z.object({
	_meta: AcpMetaSchema,
	toolCallId: AcpToolCallIdSchema,
	title: z.string().nullable().optional(),
	kind: AcpToolKindSchema.nullable().optional(),
	status: AcpToolCallStatusSchema.nullable().optional(),
	rawInput: z.unknown().optional(),
	rawOutput: z.unknown().optional(),
	content: z.array(AcpToolCallContentSchema).nullable().optional(),
	locations: z.array(AcpToolCallLocationSchema).nullable().optional()
});
/**
* The type of permission option being presented to the user
*
* Helps clients choose appropriate icons and UI treatment.
*/
const AcpPermissionOptionKindSchema = z.enum([
	"allow_once",
	"allow_always",
	"reject_once",
	"reject_always"
]);
/** An option presented to the user when requesting permission */
const AcpPermissionOptionSchema = z.object({
	_meta: AcpMetaSchema,
	optionId: AcpPermissionOptionIdSchema,
	name: z.string(),
	kind: AcpPermissionOptionKindSchema
});
/** The user selected one of the provided options */
const AcpSelectedPermissionOutcomeSchema = z.object({
	_meta: AcpMetaSchema,
	outcome: z.literal("selected"),
	optionId: AcpPermissionOptionIdSchema
});
/** The prompt turn was cancelled before the user responded */
const AcpCancelledPermissionOutcomeSchema = z.object({ outcome: z.literal("cancelled") });
/**
* The outcome of a permission request - discriminated union on `outcome`
*
* Discriminated on `outcome`: "selected" | "cancelled"
*/
const AcpRequestPermissionOutcomeSchema = z.discriminatedUnion("outcome", [AcpSelectedPermissionOutcomeSchema, AcpCancelledPermissionOutcomeSchema]);
/**
* Request for user permission to execute a tool call
*
* @see https://agentclientprotocol.com/protocol/tool-calls#requesting-permission
*/
const AcpRequestPermissionRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	toolCall: AcpToolCallUpdateSchema,
	options: z.array(AcpPermissionOptionSchema)
});
/** Response to a permission request */
const AcpRequestPermissionResponseSchema = z.object({
	_meta: AcpMetaSchema,
	outcome: AcpRequestPermissionOutcomeSchema
});

//#endregion
//#region src/acp/updates.ts
/**
* ACP session update notification types
*
* These schemas define the real-time session updates streamed from agent to client
* during prompt processing, including message chunks, tool calls, plans, and more.
*
* @see https://agentclientprotocol.com/protocol/prompt-turn#3-agent-reports-output
*/
/** A streamed item of content */
const AcpContentChunkBaseSchema = z.object({
	_meta: AcpMetaSchema,
	content: AcpContentBlockSchema
});
/** User message chunk */
const AcpUserMessageChunkSchema = AcpContentChunkBaseSchema.extend({ sessionUpdate: z.literal("user_message_chunk") });
/** Agent message chunk */
const AcpAgentMessageChunkSchema = AcpContentChunkBaseSchema.extend({ sessionUpdate: z.literal("agent_message_chunk") });
/** Agent thought chunk (internal reasoning) */
const AcpAgentThoughtChunkSchema = AcpContentChunkBaseSchema.extend({ sessionUpdate: z.literal("agent_thought_chunk") });
/** Notification that a new tool call has been initiated */
const AcpToolCallUpdateNotificationSchema = AcpToolCallSchema.extend({ sessionUpdate: z.literal("tool_call") });
/** Update on the status or results of a tool call */
const AcpToolCallStatusUpdateSchema = AcpToolCallUpdateSchema.extend({ sessionUpdate: z.literal("tool_call_update") });
/**
* Status of a plan entry
*
* @see https://agentclientprotocol.com/protocol/agent-plan#plan-entries
*/
const AcpPlanEntryStatusSchema = z.enum([
	"pending",
	"in_progress",
	"completed"
]);
/**
* Priority levels for plan entries
*
* @see https://agentclientprotocol.com/protocol/agent-plan#plan-entries
*/
const AcpPlanEntryPrioritySchema = z.enum([
	"high",
	"medium",
	"low"
]);
/**
* A single entry in the execution plan
*
* @see https://agentclientprotocol.com/protocol/agent-plan#plan-entries
*/
const AcpPlanEntrySchema = z.object({
	_meta: AcpMetaSchema,
	content: z.string(),
	priority: AcpPlanEntryPrioritySchema,
	status: AcpPlanEntryStatusSchema
});
/**
* An execution plan for accomplishing complex tasks
*
* @see https://agentclientprotocol.com/protocol/agent-plan
*/
const AcpPlanUpdateSchema = z.object({
	_meta: AcpMetaSchema,
	sessionUpdate: z.literal("plan"),
	entries: z.array(AcpPlanEntrySchema)
});
/** Unstructured command input */
const AcpUnstructuredCommandInputSchema = z.object({
	_meta: AcpMetaSchema,
	type: z.literal("unstructured"),
	hint: z.string()
});
/** The input specification for a command */
const AcpAvailableCommandInputSchema = AcpUnstructuredCommandInputSchema;
/** Information about a command */
const AcpAvailableCommandSchema = z.object({
	_meta: AcpMetaSchema,
	name: z.string(),
	description: z.string(),
	input: AcpAvailableCommandInputSchema.nullable().optional()
});
/** Available commands are ready or have changed */
const AcpAvailableCommandsUpdateSchema = z.object({
	_meta: AcpMetaSchema,
	sessionUpdate: z.literal("available_commands_update"),
	availableCommands: z.array(AcpAvailableCommandSchema)
});
/**
* The current mode of the session has changed
*
* @see https://agentclientprotocol.com/protocol/session-modes
*/
const AcpCurrentModeUpdateSchema = z.object({
	_meta: AcpMetaSchema,
	sessionUpdate: z.literal("current_mode_update"),
	currentModeId: AcpSessionModeIdSchema
});
/** Session configuration options have been updated */
const AcpConfigOptionUpdateSchema = z.object({
	_meta: AcpMetaSchema,
	sessionUpdate: z.literal("config_option_update"),
	configOptions: z.array(AcpSessionConfigOptionSchema)
});
/** Session metadata has been updated */
const AcpSessionInfoUpdateSchema = z.object({
	_meta: AcpMetaSchema,
	sessionUpdate: z.literal("session_info_update"),
	title: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional()
});
/** Cost information for a session (UNSTABLE) */
const AcpCostSchema = z.object({
	amount: z.number(),
	currency: z.string()
});
/** Context window and cost update for a session (UNSTABLE) */
const AcpUsageUpdateSchema = z.object({
	_meta: AcpMetaSchema,
	sessionUpdate: z.literal("usage_update"),
	used: z.number().int().min(0),
	size: z.number().int().min(0),
	cost: AcpCostSchema.nullable().optional()
});
/**
* Session update - discriminated union on `sessionUpdate` field
*
* Different types of updates streamed during session processing.
* Discriminated on `sessionUpdate`: 11 variants.
*
* @see https://agentclientprotocol.com/protocol/prompt-turn#3-agent-reports-output
*/
const AcpSessionUpdateSchema = z.discriminatedUnion("sessionUpdate", [
	AcpUserMessageChunkSchema,
	AcpAgentMessageChunkSchema,
	AcpAgentThoughtChunkSchema,
	AcpToolCallUpdateNotificationSchema,
	AcpToolCallStatusUpdateSchema,
	AcpPlanUpdateSchema,
	AcpAvailableCommandsUpdateSchema,
	AcpCurrentModeUpdateSchema,
	AcpConfigOptionUpdateSchema,
	AcpSessionInfoUpdateSchema,
	AcpUsageUpdateSchema
]);
/**
* Notification containing a session update from the agent
*
* Wraps a SessionUpdate with the session ID it pertains to.
*
* @see https://agentclientprotocol.com/protocol/prompt-turn#3-agent-reports-output
*/
const AcpSessionNotificationSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	update: AcpSessionUpdateSchema
});

//#endregion
//#region src/acp/prompt.ts
/**
* ACP prompt turn types
*
* These schemas define the prompt request/response flow and related types
* like stop reasons and cancellation.
*
* @see https://agentclientprotocol.com/protocol/prompt-turn
*/
/**
* Reasons why an agent stops processing a prompt turn
*
* @see https://agentclientprotocol.com/protocol/prompt-turn#stop-reasons
*/
const AcpStopReasonSchema = z.enum([
	"end_turn",
	"max_tokens",
	"max_turn_requests",
	"refusal",
	"cancelled"
]);
/** Token usage information for a prompt turn (UNSTABLE) */
const AcpUsageSchema = z.object({
	totalTokens: z.number().int().min(0),
	inputTokens: z.number().int().min(0),
	outputTokens: z.number().int().min(0),
	cachedReadTokens: z.number().int().min(0).nullable().optional(),
	cachedWriteTokens: z.number().int().min(0).nullable().optional(),
	thoughtTokens: z.number().int().min(0).nullable().optional()
});
/**
* Request parameters for sending a user prompt to the agent
*
* @see https://agentclientprotocol.com/protocol/prompt-turn#1-user-message
*/
const AcpPromptRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	prompt: z.array(AcpContentBlockSchema)
});
/**
* Response from processing a user prompt
*
* @see https://agentclientprotocol.com/protocol/prompt-turn#4-check-for-completion
*/
const AcpPromptResponseSchema = z.object({
	_meta: AcpMetaSchema,
	stopReason: AcpStopReasonSchema,
	usage: AcpUsageSchema.nullable().optional()
});
/**
* Notification to cancel ongoing operations for a session
*
* @see https://agentclientprotocol.com/protocol/prompt-turn#cancellation
*/
const AcpCancelNotificationSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema
});

//#endregion
//#region src/acp/resources.ts
/**
* ACP client-side resource method types
*
* These schemas define file system and terminal operations that the agent
* can request from the client.
*
* @see https://agentclientprotocol.com/protocol/overview#client
* @see https://agentclientprotocol.com/protocol/terminals
*/
/**
* Request to read content from a text file
*
* Only available if the client supports the `fs.readTextFile` capability.
*/
const AcpReadTextFileRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	path: z.string(),
	line: z.number().int().min(0).nullable().optional(),
	limit: z.number().int().min(0).nullable().optional()
});
/** Response containing the contents of a text file */
const AcpReadTextFileResponseSchema = z.object({
	_meta: AcpMetaSchema,
	content: z.string()
});
/**
* Request to write content to a text file
*
* Only available if the client supports the `fs.writeTextFile` capability.
*/
const AcpWriteTextFileRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	path: z.string(),
	content: z.string()
});
/** Response to write text file (empty on success) */
const AcpWriteTextFileResponseSchema = z.object({ _meta: AcpMetaSchema });
/**
* Request to create a new terminal and execute a command
*
* Only available if the `terminal` client capability is set to true.
*
* @see https://agentclientprotocol.com/protocol/terminals
*/
const AcpCreateTerminalRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	command: z.string(),
	args: z.array(z.string()).optional(),
	cwd: z.string().nullable().optional(),
	env: z.array(AcpEnvVariableSchema).optional(),
	outputByteLimit: z.number().int().min(0).nullable().optional()
});
/** Response containing the ID of the created terminal */
const AcpCreateTerminalResponseSchema = z.object({
	_meta: AcpMetaSchema,
	terminalId: z.string()
});
/**
* Request to get the current output and status of a terminal
*
* @see https://agentclientprotocol.com/protocol/terminals
*/
const AcpTerminalOutputRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	terminalId: z.string()
});
/** Exit status of a terminal command */
const AcpTerminalExitStatusSchema = z.object({
	_meta: AcpMetaSchema,
	exitCode: z.number().int().min(0).nullable().optional(),
	signal: z.string().nullable().optional()
});
/** Response containing the terminal output and exit status */
const AcpTerminalOutputResponseSchema = z.object({
	_meta: AcpMetaSchema,
	output: z.string(),
	truncated: z.boolean(),
	exitStatus: AcpTerminalExitStatusSchema.nullable().optional()
});
/** Request to wait for a terminal command to exit */
const AcpWaitForTerminalExitRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	terminalId: z.string()
});
/** Response containing the exit status of a terminal command */
const AcpWaitForTerminalExitResponseSchema = z.object({
	_meta: AcpMetaSchema,
	exitCode: z.number().int().min(0).nullable().optional(),
	signal: z.string().nullable().optional()
});
/** Request to kill a terminal command without releasing the terminal */
const AcpKillTerminalCommandRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	terminalId: z.string()
});
/** Response to terminal/kill command method */
const AcpKillTerminalCommandResponseSchema = z.object({ _meta: AcpMetaSchema });
/** Request to release a terminal and free its resources */
const AcpReleaseTerminalRequestSchema = z.object({
	_meta: AcpMetaSchema,
	sessionId: AcpSessionIdSchema,
	terminalId: z.string()
});
/** Response to terminal/release method */
const AcpReleaseTerminalResponseSchema = z.object({ _meta: AcpMetaSchema });

//#endregion
export { ACP_ERROR_CODES, AcpAgentCapabilitiesSchema, AcpAgentMessageChunkSchema, AcpAgentThoughtChunkSchema, AcpAnnotationsSchema, AcpAudioContentSchema, AcpAuthMethodSchema, AcpAuthenticateRequestSchema, AcpAuthenticateResponseSchema, AcpAvailableCommandInputSchema, AcpAvailableCommandSchema, AcpAvailableCommandsUpdateSchema, AcpBlobResourceContentsSchema, AcpCancelNotificationSchema, AcpCancelledPermissionOutcomeSchema, AcpClientCapabilitiesSchema, AcpConfigOptionUpdateSchema, AcpContentBlockSchema, AcpCostSchema, AcpCreateTerminalRequestSchema, AcpCreateTerminalResponseSchema, AcpCurrentModeUpdateSchema, AcpEmbeddedResourceResourceSchema, AcpEmbeddedResourceSchema, AcpEnvVariableSchema, AcpErrorCodeSchema, AcpErrorSchema, AcpFileSystemCapabilitySchema, AcpForkSessionRequestSchema, AcpForkSessionResponseSchema, AcpHttpHeaderSchema, AcpImageContentSchema, AcpImplementationSchema, AcpInitializeRequestSchema, AcpInitializeResponseSchema, AcpJsonRpcErrorResponseSchema, AcpJsonRpcNotificationSchema, AcpJsonRpcRequestSchema, AcpJsonRpcResponseSchema, AcpKillTerminalCommandRequestSchema, AcpKillTerminalCommandResponseSchema, AcpListSessionsRequestSchema, AcpListSessionsResponseSchema, AcpLoadSessionRequestSchema, AcpLoadSessionResponseSchema, AcpMcpCapabilitiesSchema, AcpMcpServerHttpSchema, AcpMcpServerSchema, AcpMcpServerSseSchema, AcpMcpServerStdioSchema, AcpMetaSchema, AcpModelIdSchema, AcpModelInfoSchema, AcpNewSessionRequestSchema, AcpNewSessionResponseSchema, AcpPermissionOptionIdSchema, AcpPermissionOptionKindSchema, AcpPermissionOptionSchema, AcpPlanEntryPrioritySchema, AcpPlanEntrySchema, AcpPlanEntryStatusSchema, AcpPlanUpdateSchema, AcpPromptCapabilitiesSchema, AcpPromptRequestSchema, AcpPromptResponseSchema, AcpProtocolVersionSchema, AcpReadTextFileRequestSchema, AcpReadTextFileResponseSchema, AcpReleaseTerminalRequestSchema, AcpReleaseTerminalResponseSchema, AcpRequestIdSchema, AcpRequestPermissionOutcomeSchema, AcpRequestPermissionRequestSchema, AcpRequestPermissionResponseSchema, AcpResourceLinkSchema, AcpResumeSessionRequestSchema, AcpResumeSessionResponseSchema, AcpRoleSchema, AcpSelectedPermissionOutcomeSchema, AcpSessionCapabilitiesSchema, AcpSessionConfigGroupIdSchema, AcpSessionConfigIdSchema, AcpSessionConfigOptionCategorySchema, AcpSessionConfigOptionSchema, AcpSessionConfigSelectGroupSchema, AcpSessionConfigSelectOptionSchema, AcpSessionConfigSelectOptionsSchema, AcpSessionConfigValueIdSchema, AcpSessionForkCapabilitiesSchema, AcpSessionIdSchema, AcpSessionInfoSchema, AcpSessionInfoUpdateSchema, AcpSessionListCapabilitiesSchema, AcpSessionModeIdSchema, AcpSessionModeSchema, AcpSessionModeStateSchema, AcpSessionModelStateSchema, AcpSessionNotificationSchema, AcpSessionResumeCapabilitiesSchema, AcpSessionUpdateSchema, AcpSetSessionConfigOptionRequestSchema, AcpSetSessionConfigOptionResponseSchema, AcpSetSessionModeRequestSchema, AcpSetSessionModeResponseSchema, AcpSetSessionModelRequestSchema, AcpSetSessionModelResponseSchema, AcpStopReasonSchema, AcpTerminalExitStatusSchema, AcpTerminalOutputRequestSchema, AcpTerminalOutputResponseSchema, AcpTextContentSchema, AcpTextResourceContentsSchema, AcpToolCallContentContentSchema, AcpToolCallContentDiffSchema, AcpToolCallContentSchema, AcpToolCallContentTerminalSchema, AcpToolCallIdSchema, AcpToolCallLocationSchema, AcpToolCallSchema, AcpToolCallStatusSchema, AcpToolCallStatusUpdateSchema, AcpToolCallUpdateNotificationSchema, AcpToolCallUpdateSchema, AcpToolKindSchema, AcpUnstructuredCommandInputSchema, AcpUsageSchema, AcpUsageUpdateSchema, AcpUserMessageChunkSchema, AcpWaitForTerminalExitRequestSchema, AcpWaitForTerminalExitResponseSchema, AcpWriteTextFileRequestSchema, AcpWriteTextFileResponseSchema, AddSessionShareRequestSchema, ApiArchiveSessionSchema, ApiDeleteArtifactSchema, ApiDeleteMachineSchema, ApiDeleteSessionSchema, ApiEphemeralAcpPermissionRequestSchema, ApiEphemeralAcpSessionCommandResponseSchema, ApiEphemeralAcpSessionCommandSchema, ApiEphemeralAcpSessionUpdateSchema, ApiEphemeralActivityUpdateSchema, ApiEphemeralFriendStatusUpdateSchema, ApiEphemeralMachineActivityUpdateSchema, ApiEphemeralMachineDisconnectedUpdateSchema, ApiEphemeralMachineStatusUpdateSchema, ApiEphemeralUpdateSchema, ApiEphemeralUsageUpdateSchema, ApiKvBatchUpdateSchema, ApiMessageSchema, ApiNewArtifactSchema, ApiNewFeedPostSchema, ApiNewMachineSchema, ApiRelationshipUpdatedSchema, ApiUpdateAccountSchema, ApiUpdateArtifactSchema, ApiUpdateContainerSchema, ApiUpdateMachineStateSchema, ApiUpdateNewMessageSchema, ApiUpdateNewSessionSchema, ApiUpdateSchema, ApiUpdateSessionStateSchema, ArchiveReasonSchema, BODY_SIZE_LIMITS, EncryptedContentSchema, EphemeralPayloadSchema, FeedBodySchema, GitHubProfileSchema, ImageRefSchema, InvitationStatusSchema, McpServerStateSchema, McpSyncStateSchema, McpToolInfoSchema, NullableVersionedValueSchema, PATTERNS, PlanLimitsResponseSchema, RelationshipStatusSchema, RemoveSessionShareRequestSchema, ResendInvitationRequestSchema, RevokeInvitationRequestSchema, STRING_LIMITS, SessionShareEntrySchema, SessionShareInvitationSchema, SessionSharePermissionSchema, SessionShareSettingsSchema, SessionShareUrlConfigSchema, UpdatePayloadSchema, UpdateSessionShareRequestSchema, UpdateUrlSharingRequestSchema, UsageLimitSchema, UserProfileSchema, VersionedValueSchema, getMachineId, getMachineIdFromEphemeral, getSessionId, getSessionIdFromEphemeral, hasMachineId, hasMachineIdEphemeral, hasSessionId, hasSessionIdEphemeral, makeOpenApiGitHubProfileSchema, tryGetMachineId, tryGetMachineIdFromEphemeral, tryGetSessionId, tryGetSessionIdFromEphemeral };
//# sourceMappingURL=index.mjs.map